import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square, Upload, Loader2, Trash2, Pencil, Radio, Users, ShieldCheck, CalendarPlus, Clock, Image as ImageIcon, PhoneCall, PhoneOff, PhoneMissed, Hand, UserPlus, Volume2, Save, X, Star, AlertTriangle, Copy, ArrowDownWideNarrow, Wand2, Music, Gauge, Headphones, Eye, Inbox, Check, MapPin, CalendarClock, Plus, ScrollText, Crown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { createLiveStudio } from "@/lib/webrtc";
import { Device } from "@twilio/voice-sdk";
import FloatingReactions from "@/components/FloatingReactions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RecordingReview from "@/components/RecordingReview";
import { fmtDate, fmtDateTime } from "@/lib/date";

const fmt = (iso) => fmtDateTime(iso);

export default function HostStudio() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [live, setLive] = useState(false);
  const [liveSid, setLiveSid] = useState(null);
  const [count, setCount] = useState(0);
  const [title, setTitle] = useState("In His Image Gospel Show");
  const [callins, setCallins] = useState([]);
  const [roster, setRoster] = useState([]);
  const [callerLevels, setCallerLevels] = useState({});
  const [episodes, setEpisodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [sched, setSched] = useState({ title: "In His Image Gospel Show", scheduled_at: "", description: "", reminder_minutes: 10 });
  const [showSchedule, setShowSchedule] = useState(false);
  const [epTitle, setEpTitle] = useState("");
  const [epDesc, setEpDesc] = useState("");
  const [epSeries, setEpSeries] = useState("");
  const [uploading, setUploading] = useState(false);
  const epFileRef = useRef(null);
  const coverRef = useRef(null);
  const streamRef = useRef(null);
  const bcRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const airedTracksRef = useRef([]);
  const recordStartRef = useRef(null);
  const [recordOn, setRecordOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [micLevel, setMicLevelState] = useState(100);
  const [meter, setMeter] = useState(0);
  const [hpLevel, setHpLevelState] = useState(0);
  const [monMeter, setMonMeter] = useState(0);
  const [outputDevices, setOutputDevices] = useState([]);
  const [outputDeviceId, setOutputDeviceId] = useState("");
  const [inputDevices, setInputDevices] = useState([]);
  const [micDeviceId, setMicDeviceId] = useState("");
  const [feedbackGuard, setFeedbackGuard] = useState(true);
  const [fbTriggered, setFbTriggered] = useState(false);
  const [noiseGate, setNoiseGateState] = useState(true);
  const [gateSensitivity, setGateSensitivityState] = useState("medium");
  const changeNoiseGate = (v) => { setNoiseGateState(v); if (bcRef.current?.setNoiseGate) bcRef.current.setNoiseGate(v); persistMemory({ noise_gate: v }); };
  const changeGateSensitivity = (v) => { setGateSensitivityState(v); if (bcRef.current?.setGateSensitivity) bcRef.current.setGateSensitivity(v); persistMemory({ gate_sensitivity: v }); };
  const MIC_PRESETS = [["Soft-spoken", 150], ["Normal", 100], ["Loud room", 70]];
  const applyMicPreset = (name, val) => { changeMicLevel(val); toast.success(`Mic set for a ${name.toLowerCase()} voice`); };
  const [soundProfiles, setSoundProfiles] = useState([]);
  const previewMeterRef = useRef(0);
  const [learning, setLearning] = useState(false);
  const toastedProfilesRef = useRef(new Set());
  const loadSoundProfiles = (applyDefault = false) => api.get("/live/sound-profiles").then((r) => {
    setSoundProfiles(r.data);
    if (applyDefault) { const d = r.data.find((p) => p.is_default); if (d) applySoundProfile(d); }
    const fresh = r.data.filter((p) => p.is_new && !toastedProfilesRef.current.has(p.id));
    if (fresh.length) {
      fresh.forEach((p) => toastedProfilesRef.current.add(p.id));
      if (fresh.length === 1) {
        const p = fresh[0];
        toast.message(`New shared level "${p.name}"${p.shared_by ? ` from ${p.shared_by}` : ""} — try it on air`, { icon: "🎚️", action: { label: "Try it", onClick: () => applySoundProfile(p) } });
      } else {
        toast.message(`${fresh.length} new shared levels are ready — try them on air`, { icon: "🎚️" });
      }
      api.post("/live/sound-profiles/mark-seen", { ids: fresh.map((p) => p.id) }).catch(() => {});
    }
  }).catch(() => {});
  const [inbox, setInbox] = useState([]);
  const loadInbox = (notify = false) => api.get("/live/sound-profiles/inbox").then((r) => { setInbox(r.data); if (notify && r.data.length) toast.message(r.data.length === 1 ? `New shared profile "${r.data[0].name}"${r.data[0].shared_by ? ` from ${r.data[0].shared_by}` : ""}` : `${r.data.length} shared sound profiles waiting for you`); }).catch(() => {});
  const acceptInbox = async (p) => {
    try { await api.post(`/live/sound-profiles/${p.id}/accept`); toast.success(`Added "${p.name}"`); loadInbox(); loadSoundProfiles(); }
    catch (e) { toast.error("Could not add profile"); }
  };
  const dismissInbox = async (p) => {
    try { await api.delete(`/live/sound-profiles/${p.id}`); toast.success("Dismissed"); loadInbox(); }
    catch (e) { toast.error("Could not dismiss"); }
  };
  const [previewProfile, setPreviewProfile] = useState(null);
  const describeSettings = (s) => {
    s = s || {};
    const rows = [];
    if (s.mic_level != null) rows.push(["Mic level", `${s.mic_level}%`]);
    if (s.noise_gate != null) rows.push(["Noise gate", s.noise_gate ? `On · ${s.gate_sensitivity || "medium"}` : "Off"]);
    if (s.hp_level != null) rows.push(["Headphone", s.hp_level === 0 ? "Muted" : `${s.hp_level}%`]);
    if (s.auto_duck != null) rows.push(["Auto-duck", s.auto_duck ? `On · ${s.duck_depth || "medium"}` : "Off"]);
    if (s.auto_level != null) rows.push(["Auto-level callers", s.auto_level ? `On · ${s.target_loudness || "broadcast"}` : "Off"]);
    if (s.mic_device_id) { const d = inputDevices.find((x) => x.deviceId === s.mic_device_id); rows.push(["Microphone", d?.label || "Saved device"]); }
    if (s.output_device_id) { const d = outputDevices.find((x) => x.deviceId === s.output_device_id); rows.push(["Output", d?.label || "Saved device"]); }
    return rows;
  };
  const currentSoundSettings = () => ({ mic_level: micLevel, noise_gate: noiseGate, gate_sensitivity: gateSensitivity, hp_level: hpLevel, auto_duck: autoDuck, auto_level: autoLevel, duck_depth: duckDepth, target_loudness: targetLoudness, mic_device_id: micDeviceId, output_device_id: outputDeviceId });
  const saveSoundProfile = async () => {
    const name = window.prompt('Name this sound setup (e.g. "Studio", "Home", "Phone")');
    if (!name || !name.trim()) return;
    try { await api.post("/live/sound-profiles", { name: name.trim(), settings: currentSoundSettings() }); toast.success(`Saved "${name.trim()}"`); loadSoundProfiles(); }
    catch (e) { toast.error("Could not save profile"); }
  };
  const applySoundSettings = (s) => {
    s = s || {};
    if (s.mic_level != null) changeMicLevel(s.mic_level);
    if (s.noise_gate != null) changeNoiseGate(s.noise_gate);
    if (s.gate_sensitivity) changeGateSensitivity(s.gate_sensitivity);
    if (s.hp_level != null) changeHpLevel(s.hp_level);
    if (s.auto_duck != null) changeAutoDuck(s.auto_duck);
    if (s.auto_level != null) changeAutoLevel(s.auto_level);
    if (s.duck_depth) changeDuckDepth(s.duck_depth);
    if (s.target_loudness) changeTargetLoudness(s.target_loudness);
    if (s.mic_device_id != null) changeMic(s.mic_device_id);
    if (s.output_device_id != null) applySink(s.output_device_id);
  };
  const applySoundProfile = (p) => { applySoundSettings(p.settings); toast.success(`Loaded "${p.name}" sound profile`); };
  const shareSoundProfile = async (p) => {
    const email = window.prompt("Co-host's email to share this sound setup with:");
    if (!email || !email.trim()) return;
    try { const { data } = await api.post(`/live/sound-profiles/${p.id}/share`, { email: email.trim() }); toast.success(`Shared "${p.name}" with ${data.shared_with}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not share"); }
  };
  const deleteSoundProfile = async (p) => {
    if (!window.confirm(`Delete sound profile "${p.name}"?`)) return;
    try { await api.delete(`/live/sound-profiles/${p.id}`); toast.success("Profile deleted"); loadSoundProfiles(); }
    catch (e) { toast.error("Could not delete"); }
  };
  const toggleDefaultProfile = async (p) => {
    try {
      const { data } = await api.post(`/live/sound-profiles/${p.id}/default`, { is_default: !p.is_default });
      setSoundProfiles(data);
      toast.success(!p.is_default ? `"${p.name}" loads when you open the studio` : "Default cleared");
    } catch (e) { toast.error("Could not update default"); }
  };
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const startRename = (p) => { setRenamingId(p.id); setRenameValue(p.name); };
  const commitRename = async (p) => {
    const name = renameValue.trim();
    if (!name || name === p.name) { setRenamingId(null); return; }
    try { await api.put(`/live/sound-profiles/${p.id}`, { name }); setRenamingId(null); toast.success(`Renamed to "${name}"`); loadSoundProfiles(); }
    catch (e) { toast.error("Could not rename level"); }
  };
  const autoLearnLevel = async () => {
    if (learning) return;
    const useLive = live && bcRef.current?.getLevel;
    if (!useLive && !previewOn) { await startMicPreview(); await new Promise((r) => setTimeout(r, 500)); }
    setLearning(true);
    toast.message("Listening ~4s — please speak normally…");
    const samples = [];
    const started = Date.now();
    await new Promise((resolve) => {
      const iv = setInterval(() => {
        const v = useLive ? (bcRef.current?.getLevel?.() || 0) : previewMeterRef.current;
        if (v > 0.04) samples.push(v);
        if (Date.now() - started > 4000) { clearInterval(iv); resolve(); }
      }, 100);
    });
    setLearning(false);
    if (samples.length < 6) { toast.error("Didn't hear enough — try again and speak into the mic."); return; }
    samples.sort((a, b) => a - b);
    const lo = Math.floor(samples.length * 0.2), hi = Math.ceil(samples.length * 0.8);
    const slice = samples.slice(lo, hi);
    const avg = slice.reduce((s, v) => s + v, 0) / Math.max(1, slice.length);
    const base = useLive ? micLevel : 100;
    let suggested = Math.round(base * (0.5 / Math.max(0.02, avg)));
    suggested = Math.max(40, Math.min(200, suggested));
    changeMicLevel(suggested);
    toast.success(`Set mic to ${suggested}% based on your voice`);
  };
  const hpLevelRef = useRef(0);
  const feedbackGuardRef = useRef(true);
  const fbCountRef = useRef(0);
  const monitorAudioRef = useRef(null);
  const revertSettingsRef = useRef(null);
  const revertShowRef = useRef("");
  const [revertAvailable, setRevertAvailable] = useState(false);
  const [voiceCfg, setVoiceCfg] = useState(null);
  const [callInJoined, setCallInJoined] = useState(false);
  const [callInBusy, setCallInBusy] = useState(false);
  const [phoneCallers, setPhoneCallers] = useState([]);
  const [phoneMeter, setPhoneMeter] = useState(0);
  const [phoneLevel, setPhoneLevelState] = useState(100);
  const [callerMusicLevel, setCallerMusicLevelState] = useState(100);
  const [bcDelay, setBcDelayState] = useState(0);
  const [chimeOn, setChimeOn] = useState(() => (typeof localStorage !== "undefined" ? localStorage.getItem("chimeOn") !== "0" : true));
  const [chimeSound, setChimeSound] = useState(() => (typeof localStorage !== "undefined" ? localStorage.getItem("chimeSound") : null) || "chime");
  const [chimeVol, setChimeVol] = useState(() => Number((typeof localStorage !== "undefined" ? localStorage.getItem("chimeVol") : null) || 60));
  const [flaggedWords, setFlaggedWords] = useState("");
  const [greeting, setGreeting] = useState("");
  const [greetingHasAudio, setGreetingHasAudio] = useState(false);
  const [lineClosed, setLineClosed] = useState(false);
  const [lineMsg, setLineMsg] = useState("");
  const [lineBusy, setLineBusy] = useState(false);
  const [selectedCaller, setSelectedCaller] = useState(null);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [windows, setWindows] = useState([]);
  const [schedBusy, setSchedBusy] = useState(false);
  useEffect(() => { api.get("/voice/schedule").then((r) => { setAutoEnabled(!!r.data.auto_enabled); setWindows(r.data.windows || []); }).catch(() => {}); }, []);
  const saveSchedule = async (nextAuto, nextWindows) => {
    setSchedBusy(true);
    try {
      const { data } = await api.post("/voice/schedule", { auto_enabled: nextAuto, windows: nextWindows });
      setAutoEnabled(!!data.auto_enabled); setWindows(data.windows || []); setLineClosed(!!data.closed);
      setVoiceCfg((prev) => (prev ? { ...prev, closed: !!data.closed } : prev));
      toast.success(data.auto_enabled ? "Auto schedule on — the line opens & closes on its own" : "Auto schedule off");
    } catch (e) { toast.error("Could not save the schedule"); }
    finally { setSchedBusy(false); }
  };
  const toggleAuto = () => { const n = !autoEnabled; setAutoEnabled(n); saveSchedule(n, windows); };
  const addWindow = () => { const w = [...windows, { day: "Every day", start: "09:00", end: "11:00" }]; setWindows(w); saveSchedule(autoEnabled, w); };
  const updateWindow = (i, patch) => { const w = windows.map((x, idx) => idx === i ? { ...x, ...patch } : x); setWindows(w); };
  const removeWindow = (i) => { const w = windows.filter((_, idx) => idx !== i); setWindows(w); saveSchedule(autoEnabled, w); };
  const [dumpLog, setDumpLog] = useState([]);
  const waitSinceRef = useRef({});
  useEffect(() => { api.get("/voice/greeting").then((r) => { setGreeting(r.data.text || ""); setGreetingHasAudio(!!r.data.has_audio); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/voice/line-status").then((r) => { setLineClosed(!!r.data.closed); setLineMsg(r.data.message || ""); }).catch(() => {}); }, []);
  const toggleLineClosed = async () => {
    const next = !lineClosed;
    setLineBusy(true);
    try {
      const { data } = await api.post("/voice/line-status", { closed: next, message: lineMsg });
      setLineClosed(!!data.closed); setLineMsg(data.message || "");
      setVoiceCfg((prev) => (prev ? { ...prev, closed: !!data.closed } : prev));
      toast.success(data.closed ? "Call-in line closed — callers hear the closed message" : "Call-in line is open");
    } catch (e) { toast.error("Could not update the call-in line"); }
    finally { setLineBusy(false); }
  };
  const saveGreeting = async () => {
    try { const { data } = await api.post("/voice/greeting", { text: greeting }); setGreeting(data.text || ""); toast.success(data.text ? "Custom greeting saved" : "Greeting reset to default"); }
    catch (e) { toast.error("Could not save greeting"); }
  };
  const uploadGreetingAudio = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/voice/greeting-audio", fd, { headers: { "Content-Type": "multipart/form-data" } }); setGreetingHasAudio(true); toast.success("Greeting clip uploaded — callers will hear it"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not upload clip"); }
  };
  const removeGreetingAudio = async () => {
    try { await api.delete("/voice/greeting-audio"); setGreetingHasAudio(false); toast.success("Greeting clip removed"); }
    catch (e) { toast.error("Could not remove"); }
  };
  const [holdText, setHoldText] = useState("");
  const [holdHasAudio, setHoldHasAudio] = useState(false);
  const [holdUploading, setHoldUploading] = useState(false);
  const [holdUploadPct, setHoldUploadPct] = useState(0);
  const holdUploadAbortRef = useRef(null);
  const [holdLibrary, setHoldLibrary] = useState([]);
  const [holdActivePath, setHoldActivePath] = useState("");
  const [holdNowPlaying, setHoldNowPlaying] = useState("");
  const [holdStationId, setHoldStationId] = useState("");
  const [holdMode, setHoldMode] = useState("pad");
  const [holdShuffle, setHoldShuffle] = useState(false);
  const [muteHoldInHeadset, setMuteHoldInHeadset] = useState(() => { try { return localStorage.getItem("muteHoldInHeadset") === "1"; } catch (e) { return false; } });
  useEffect(() => { try { localStorage.setItem("muteHoldInHeadset", muteHoldInHeadset ? "1" : "0"); } catch (e) {} }, [muteHoldInHeadset]);
  const [holdClipBusy, setHoldClipBusy] = useState(false);
  const [stations, setStations] = useState([]);
  useEffect(() => { api.get("/stations").then((r) => setStations(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/voice/hold").then((r) => { setHoldText(r.data.text || ""); setHoldHasAudio(!!r.data.has_audio); setHoldStationId(r.data.station_id || ""); setHoldMode(r.data.hold_mode || "pad"); setHoldShuffle(!!r.data.rotate_shuffle); }).catch(() => {}); }, []);
  const changeShuffle = (v) => { setHoldShuffle(v); api.post("/voice/hold", { rotate_shuffle: v }).catch(() => {}); toast.success(v ? "Shuffle on — tracks play in random order" : "Shuffle off — tracks play in order"); };
  const saveHold = async () => {
    try { const { data } = await api.post("/voice/hold", { text: holdText, station_id: holdStationId }); setHoldText(data.text || ""); setHoldStationId(data.station_id || ""); toast.success("Hold message saved"); }
    catch (e) { toast.error("Could not save hold message"); }
  };
  const changeHoldStation = async (id) => {
    setHoldStationId(id);
    try { await api.post("/voice/hold", { station_id: id }); toast.success(id ? "Callers on hold will hear that station" : "Hold music set to the soft pad"); }
    catch (e) { toast.error("Could not update hold music"); }
  };
  const changeHoldMode = async (mode) => {
    if (mode === "custom") return;
    setHoldMode(mode); setHoldHasAudio(false);
    try { await api.post("/voice/hold", { hold_mode: mode }); loadHoldLibrary(); toast.success(mode === "silence" ? "Callers will wait in silence" : mode === "rotate" ? "Callers will hear your library on rotation" : "Callers hear the soft ambient pad"); }
    catch (e) { toast.error("Could not update hold music"); }
  };
  const uploadHoldAudio = async (file) => {
    if (!file) return;
    setHoldUploading(true); setHoldUploadPct(0);
    const controller = new AbortController();
    holdUploadAbortRef.current = controller;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/voice/hold-library", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.signal,
        onUploadProgress: (e) => { if (e.total) setHoldUploadPct(Math.round((e.loaded / e.total) * 100)); },
      });
      if (data.activated) setHoldHasAudio(true);
      toast.success(data.activated ? "Track added — now playing to callers" : "Track added to your library");
      loadHoldLibrary();
    } catch (e) {
      if (e.code === "ERR_CANCELED" || e.name === "CanceledError") toast.message("Upload canceled");
      else toast.error(e.response?.data?.detail || "Could not upload clip");
    }
    finally { setHoldUploading(false); setHoldUploadPct(0); holdUploadAbortRef.current = null; }
  };
  const cancelHoldUpload = () => { if (holdUploadAbortRef.current) holdUploadAbortRef.current.abort(); };
  const removeHoldAudio = async () => {
    try { await api.delete("/voice/hold-audio"); setHoldHasAudio(false); toast.success("Hold clip removed"); }
    catch (e) { toast.error("Could not remove"); }
  };
  const generateHoldClip = async () => {
    setHoldClipBusy(true);
    try {
      const msg = holdText.trim() || "We appreciate your call. Please hold, and we'll be with you momentarily.";
      await api.post("/voice/hold-clip", { message: msg, seconds: 90 });
      setHoldHasAudio(true);
      toast.success("Gospel hold clip created — callers hear it on a 1:30 loop");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the hold clip"); }
    finally { setHoldClipBusy(false); }
  };
  const greetPreviewRef = useRef(null);
  const [greetPreviewing, setGreetPreviewing] = useState(false);
  const stopGreetPreview = () => {
    try { greetPreviewRef.current?.pause(); greetPreviewRef.current = null; } catch (e) {}
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
    setGreetPreviewing(false);
  };
  const mediaSrc = async (path) => {
    try {
      const { data } = await api.get("/voice/media-token");
      return `${api.defaults.baseURL}${path}?t=${encodeURIComponent(data.token)}`;
    } catch (e) {
      return `${api.defaults.baseURL}${path}`;
    }
  };
  const previewGreeting = async () => {
    if (greetPreviewing) { stopGreetPreview(); return; }
    if (greetingHasAudio) {
      const a = new Audio(await mediaSrc("/voice/greeting-audio"));
      a.onended = () => setGreetPreviewing(false);
      a.onerror = () => { setGreetPreviewing(false); toast.error("Could not play that preview"); };
      greetPreviewRef.current = a;
      a.play().then(() => setGreetPreviewing(true)).catch(() => { setGreetPreviewing(false); toast.error("Could not play that preview"); });
      return;
    }
    try {
      const line = greeting.trim() || "Hey there — welcome to In His Image Radio! So glad you called. Please say your first name and the city you're calling from, then hang tight — we may bring you on the air in a little bit.";
      window.speechSynthesis && window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(line);
      u.onend = () => setGreetPreviewing(false);
      window.speechSynthesis && window.speechSynthesis.speak(u);
      setGreetPreviewing(true);
    } catch (e) { toast.error("Preview not supported in this browser"); }
  };
  const holdPreviewRef = useRef(null);
  const [holdPreviewing, setHoldPreviewing] = useState(false);
  const [callerPreviewOn, setCallerPreviewOn] = useState(false);
  const stopHoldPreview = () => {
    try { holdPreviewRef.current?.pause(); holdPreviewRef.current = null; } catch (e) {}
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
    setHoldPreviewing(false); setCallerPreviewOn(false);
  };
  const previewHold = async () => {
    if (holdPreviewing) { stopHoldPreview(); return; }
    let src = await mediaSrc("/voice/hold-music");
    if (holdHasAudio) src = await mediaSrc("/voice/hold-audio");
    else if (holdStationId) { const st = stations.find((s) => s.id === holdStationId); if (st?.stream_url) src = st.stream_url; }
    if (!holdHasAudio && !holdStationId) {
      try {
        const line = holdText.trim() || "You're connected to In His Image Radio. Please stay on the line — we'll be right with you.";
        window.speechSynthesis && window.speechSynthesis.cancel();
        window.speechSynthesis && window.speechSynthesis.speak(new SpeechSynthesisUtterance(line));
      } catch (e) {}
    }
    const a = new Audio(src);
    a.onended = () => setHoldPreviewing(false);
    a.onerror = () => { setHoldPreviewing(false); toast.error("Could not play that preview"); };
    holdPreviewRef.current = a;
    a.play().then(() => setHoldPreviewing(true)).catch(() => { setHoldPreviewing(false); toast.error("Could not play that preview"); });
  };
  const loadHoldLibrary = () => api.get("/voice/hold-library").then((r) => { setHoldLibrary(r.data.tracks || []); setHoldActivePath(r.data.active_path || ""); setHoldNowPlaying(r.data.now_playing_id || ""); }).catch(() => {});
  const activateTrack = async (id) => { try { await api.post(`/voice/hold-library/${id}/activate`); setHoldHasAudio(true); setHoldMode("custom"); toast.success("Now playing to callers on hold"); loadHoldLibrary(); } catch (e) { toast.error("Could not switch track"); } };
  const deleteTrack = async (id) => { try { await api.delete(`/voice/hold-library/${id}`); toast.success("Track removed"); loadHoldLibrary(); } catch (e) { toast.error("Could not remove"); } };
  const resetPlays = async () => { try { await api.post("/voice/hold-library/reset-plays"); toast.success("Play counts reset"); loadHoldLibrary(); } catch (e) { toast.error("Could not reset"); } };
  const previewTrack = async (id) => {
    if (holdPreviewing) { stopHoldPreview(); return; }
    const a = new Audio(await mediaSrc(`/voice/hold-library/${id}/audio`));
    a.onended = () => setHoldPreviewing(false);
    a.onerror = () => { setHoldPreviewing(false); toast.error("Could not play that preview"); };
    holdPreviewRef.current = a;
    a.play().then(() => setHoldPreviewing(true)).catch(() => { setHoldPreviewing(false); toast.error("Could not play that preview"); });
  };
  // One-tap: play exactly what a caller on hold hears right now, mirroring /voice/wait.
  const previewCallerHold = async () => {
    if (callerPreviewOn) { stopHoldPreview(); return; }
    let mode = holdMode, activePath = holdActivePath, nowId = holdNowPlaying, lib = holdLibrary, hasAudio = holdHasAudio, text = holdText;
    try {
      const [{ data: h }, { data: l }] = await Promise.all([api.get("/voice/hold"), api.get("/voice/hold-library")]);
      mode = h.hold_mode || "pad"; hasAudio = !!h.has_audio; text = h.text || "";
      activePath = l.active_path || ""; nowId = l.now_playing_id || ""; lib = l.tracks || [];
      setHoldMode(mode); setHoldHasAudio(hasAudio); setHoldActivePath(activePath); setHoldNowPlaying(nowId); setHoldLibrary(lib); setHoldText(text);
    } catch (e) {}
    if (mode === "silence" && !activePath) {
      // Callers hear the spoken reassurance (if any), then silence.
      stopHoldPreview();
      const line = (text || "").trim();
      if (line) { try { window.speechSynthesis.cancel(); const u = new SpeechSynthesisUtterance(line); u.onend = () => setCallerPreviewOn(false); window.speechSynthesis.speak(u); setCallerPreviewOn(true); toast.message("Callers hear your reassurance, then wait in silence"); } catch (e) { toast.message("Right now callers wait in silence — no hold music."); } }
      else toast.message("Right now callers wait in silence — no hold music is playing.");
      return;
    }
    let src, padBed = false;
    if (mode === "rotate" && !activePath) {
      const tid = nowId || (lib[0] && lib[0].id);
      src = tid ? await mediaSrc(`/voice/hold-library/${tid}/audio`) : await mediaSrc("/voice/hold-music");
    } else if (activePath || hasAudio) {
      src = await mediaSrc("/voice/hold-audio");
    } else {
      // Ambient-pad / default mode: callers also hear the spoken reassurance over the pad.
      src = await mediaSrc("/voice/hold-music");
      padBed = true;
    }
    stopHoldPreview();
    const a = new Audio(src);
    if (padBed) a.volume = 0.6; // duck the pad so the voice sits clearly on top, as callers hear it
    a.onended = () => setCallerPreviewOn(false);
    a.onerror = () => { setCallerPreviewOn(false); toast.error("Could not play the hold preview"); };
    holdPreviewRef.current = a;
    a.play().then(() => {
      setCallerPreviewOn(true);
      if (padBed) {
        const line = (text || "").trim() || "You're connected to In His Image Radio. Please stay on the line — we'll be right with you.";
        try { window.speechSynthesis.cancel(); window.speechSynthesis.speak(new SpeechSynthesisUtterance(line)); } catch (e) {}
        toast.message("Playing the pad and your reassurance together");
      } else {
        toast.message("Playing what callers hear on hold");
      }
    }).catch(() => { setCallerPreviewOn(false); toast.error("Could not play the hold preview"); });
  };
  const exportDumpLog = () => {
    const rows = [["kind", "word", "time"], ...dumpLog.map((e) => [e.kind, e.word || "", fmtDateTime(e.at)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "dump-log.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const heldTooLong = (sid) => { const s = waitSinceRef.current[sid]; return s && (nowTick - s) > 120000; };
  const loadDumpLog = () => api.get("/voice/dump-log").then((r) => setDumpLog(r.data.entries || [])).catch(() => {});
  const waitStr = (sid) => {
    const s = waitSinceRef.current[sid];
    if (!s) return "";
    const sec = Math.max(0, Math.floor((nowTick - s) / 1000));
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  };
  const prevCallerSidsRef = useRef([]);
  const callerOrderRef = useRef([]);
  const onAirSinceRef = useRef({});
  const screenSinceRef = useRef({});
  const dragSidRef = useRef(null);
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => { if (!voiceCfg?.configured) return; const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, [voiceCfg?.configured]);
  const elapsedStr = (sid) => {
    const s = onAirSinceRef.current[sid];
    if (!s) return "0:00";
    const sec = Math.max(0, Math.floor((nowTick - s) / 1000));
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  };
  const screenStr = (sid) => {
    const s = screenSinceRef.current[sid];
    if (!s) return "0:00";
    const sec = Math.max(0, Math.floor((nowTick - s) / 1000));
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  };
  const onDragStartCaller = (sid) => { dragSidRef.current = sid; };
  const onDropCaller = (targetSid) => {
    const from = dragSidRef.current; dragSidRef.current = null;
    if (!from || from === targetSid) return;
    const order = [...callerOrderRef.current];
    const fi = order.indexOf(from), ti = order.indexOf(targetSid);
    if (fi < 0 || ti < 0) return;
    order.splice(ti, 0, order.splice(fi, 1)[0]);
    callerOrderRef.current = order;
    setPhoneCallers((prev) => { const m = Object.fromEntries(prev.map((c) => [c.call_sid, c])); return order.map((s) => m[s]).filter(Boolean); });
  };
  const dumpPollRef = useRef(null);
  const lastDumpAtRef = useRef(null);
  const chimeOnRef = useRef(true);
  const [flashSids, setFlashSids] = useState([]);
  const [onAirFlashSids, setOnAirFlashSids] = useState([]);
  const flashTimerRef = useRef(null);
  const [missedCallers, setMissedCallers] = useState([]);
  const [autoDrop, setAutoDrop] = useState({ enabled: true, minutes: 10, action: "sms_drop" });
  const chimeSoundRef = useRef("chime");
  const chimeVolRef = useRef(60);
  const CHIME_SOUNDS = { chime: [880, 1174.7], bell: [1318.5, 1318.5], ping: [1568], knock: [196, 147], soft: [523.25, 659.25] };
  useEffect(() => { chimeOnRef.current = chimeOn; try { localStorage.setItem("chimeOn", chimeOn ? "1" : "0"); } catch (e) {} }, [chimeOn]);
  useEffect(() => { chimeSoundRef.current = chimeSound; try { localStorage.setItem("chimeSound", chimeSound); } catch (e) {} }, [chimeSound]);
  useEffect(() => { chimeVolRef.current = chimeVol; try { localStorage.setItem("chimeVol", String(chimeVol)); } catch (e) {} }, [chimeVol]);
  useEffect(() => { api.get("/voice/flagged-words").then((r) => setFlaggedWords((r.data.words || []).join(", "))).catch(() => {}); }, []);
  const saveFlaggedWords = async () => {
    try { const { data } = await api.post("/voice/flagged-words", { words: flaggedWords }); setFlaggedWords((data.words || []).join(", ")); toast.success("Flagged words saved"); }
    catch (e) { toast.error("Could not save"); }
  };
  const saveNote = async (sid, note) => {
    try { await api.post(`/voice/callers/${sid}/note`, { note }); setPhoneCallers((prev) => prev.map((x) => x.call_sid === sid ? { ...x, note } : x)); }
    catch (e) { toast.error("Could not save note"); }
  };
  const playChime = () => {
    try {
      const notes = CHIME_SOUNDS[chimeSoundRef.current] || CHIME_SOUNDS.chime;
      const vol = Math.max(0, Math.min(1, chimeVolRef.current / 100)) * 0.4;
      if (vol <= 0) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      const c = new AC();
      const g = c.createGain(); g.connect(c.destination);
      const t = c.currentTime;
      notes.forEach((f, i) => {
        const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
        const og = c.createGain(); o.connect(og); og.connect(g);
        const st = t + i * 0.16;
        og.gain.setValueAtTime(0.0001, st);
        og.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), st + 0.02);
        og.gain.exponentialRampToValueAtTime(0.0001, st + 0.4);
        o.start(st); o.stop(st + 0.42);
      });
      setTimeout(() => { try { c.close(); } catch (e) {} }, 1000);
    } catch (e) {}
  };
  // Bright ascending "you're live" confirmation chime (distinct from the waiting alert).
  const playOnAirChime = () => {
    try {
      const vol = Math.max(0, Math.min(1, chimeVolRef.current / 100)) * 0.4;
      if (vol <= 0) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      const c = new AC();
      const g = c.createGain(); g.connect(c.destination);
      const t = c.currentTime;
      [659.25, 987.77].forEach((f, i) => {
        const o = c.createOscillator(); o.type = "triangle"; o.frequency.value = f;
        const og = c.createGain(); o.connect(og); og.connect(g);
        const st = t + i * 0.11;
        og.gain.setValueAtTime(0.0001, st);
        og.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), st + 0.02);
        og.gain.exponentialRampToValueAtTime(0.0001, st + 0.32);
        o.start(st); o.stop(st + 0.34);
      });
      setTimeout(() => { try { c.close(); } catch (e) {} }, 900);
    } catch (e) {}
  };
  const confirmOnAir = (sid) => {
    setOnAirFlashSids((prev) => [...prev, sid]);
    playOnAirChime();
    setTimeout(() => setOnAirFlashSids((prev) => prev.filter((s) => s !== sid)), 1800);
  };
  const voiceDeviceRef = useRef(null);
  const voiceCallRef = useRef(null);
  const callProcessorRef = useRef(null);
  const callInJoinedRef = useRef(false);
  const phonePollRef = useRef(null);
  const phoneMeterRef = useRef(null);
  const changePhoneLevel = (v) => { setPhoneLevelState(v); if (bcRef.current?.setPhoneLevel) bcRef.current.setPhoneLevel(v / 100); persistMemory({ phone_level: v }); };
  const changeCallerMusicLevel = (v) => { setCallerMusicLevelState(v); if (bcRef.current?.setCallerMusicLevel) bcRef.current.setCallerMusicLevel(v / 100); persistMemory({ caller_music_level: v }); };
  const changeBcDelay = (sec) => { setBcDelayState(sec); if (bcRef.current?.setBroadcastDelay) bcRef.current.setBroadcastDelay(sec); toast.success(sec === 0 ? "Broadcast delay off" : `Broadcast delay set to ${sec}s`); };
  const dumpNow = () => { if (bcRef.current?.dumpBroadcast) { const d = bcRef.current.dumpBroadcast(); toast.success(d > 0 ? `Dumped the last ${Math.round(d)}s from air` : "Nothing buffered to dump"); if (d > 0) { api.post("/voice/dump-log", { kind: "manual" }).then(loadDumpLog).catch(() => {}); } } };
  // ---- Background music (library + station presets) ----
  const [bgLibrary, setBgLibrary] = useState([]);
  const [bgStations, setBgStations] = useState([]);
  const [bgPresetIds, setBgPresetIds] = useState([]);
  const [bgPlaying, setBgPlaying] = useState(null); // { url, label }
  const [bgLevel, setBgLevel] = useState(50);
  const [bgOnAir, setBgOnAir] = useState(true);
  const [bgDuck, setBgDuck] = useState(true);
  const [duckTalk, setDuckTalk] = useState(65);
  const [duckCaller, setDuckCaller] = useState(82);
  const applyDuckDepths = (t, c) => { try { bcRef.current?.setMusicDuckDepths?.(1 - t / 100, 1 - c / 100); } catch (e) {} };
  const [bgSleepMin, setBgSleepMin] = useState(0);
  const [bgSleepAt, setBgSleepAt] = useState(null);
  const [bgSleepLeft, setBgSleepLeft] = useState("");
  const bgSleepRef = useRef(null);
  const [bgMeter, setBgMeter] = useState(0);
  const [bgUploadTitle, setBgUploadTitle] = useState("");
  const [bgUploadKind, setBgUploadKind] = useState("music");
  const [bgUploading, setBgUploading] = useState(false);
  const [bgManage, setBgManage] = useState(false);
  const loadBgLibrary = () => api.get("/live/library").then((r) => setBgLibrary(r.data || [])).catch(() => {});
  const loadBgStations = () => api.get("/live/bg-stations").then((r) => setBgStations(r.data || [])).catch(() => {});
  const loadBgPresets = () => api.get("/live/bg-presets").then((r) => setBgPresetIds(r.data.station_ids || [])).catch(() => {});
  useEffect(() => { if (live) { loadBgLibrary(); loadBgStations(); loadBgPresets(); } }, [live]);
  useEffect(() => {
    if (!bgPlaying) { setBgMeter(0); return; }
    const t = setInterval(() => { if (bcRef.current?.getMusicLevel) setBgMeter(bcRef.current.getMusicLevel()); }, 150);
    return () => clearInterval(t);
  }, [bgPlaying]);
  useEffect(() => {
    if (!bgSleepAt) { setBgSleepLeft(""); return; }
    const tick = () => { const ms = bgSleepAt - Date.now(); if (ms <= 0) { setBgSleepLeft(""); return; } setBgSleepLeft(`${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`); };
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, [bgSleepAt]);
  const uploadBgLibrary = async (file) => {
    if (!file) return;
    setBgUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", bgUploadKind);
    fd.append("title", bgUploadTitle.trim() || file.name.replace(/\.[^.]+$/, ""));
    try { await api.post("/live/library", fd, { headers: { "Content-Type": "multipart/form-data" } }); setBgUploadTitle(""); toast.success("Added to your library"); loadBgLibrary(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not upload"); }
    finally { setBgUploading(false); }
  };
  const deleteBgLibrary = async (id) => {
    try { await api.delete(`/live/library/${id}`); if (bgPlaying?.url?.includes(`/library/${id}/`)) stopBg(); toast.success("Removed"); loadBgLibrary(); }
    catch (e) { toast.error("Could not remove"); }
  };
  const pushNowPlaying = (title, kind) => {
    api.post("/live/now-playing", { title: title || "", kind: kind || "music" }).catch(() => {});
    if (title) { const last = airedTracksRef.current[airedTracksRef.current.length - 1]; if (!last || last.title !== title) airedTracksRef.current.push({ title, kind: kind || "music", at: recordStartRef.current ? Math.max(0, Math.round((Date.now() - recordStartRef.current) / 1000)) : 0 }); }
  };
  const playBg = (url, label, loop, kind) => {
    if (!bcRef.current?.playBackgroundMusic) { toast.error("Go live first"); return; }
    const ok = bcRef.current.playBackgroundMusic(url, { loop });
    if (!ok) { toast.error("Could not start that track"); return; }
    bcRef.current.setMusicLevel(bgLevel / 100);
    bcRef.current.setMusicOnAir(bgOnAir);
    bcRef.current.setMusicDuck(bgDuck);
    applyDuckDepths(duckTalk, duckCaller);
    setBgPlaying({ url, label });
    pushNowPlaying(kind ? label : "", kind); // stations (no kind) clear the on-air track label
    toast.success(`Background: ${label}`);
  };
  const clearBgSleep = () => { if (bgSleepRef.current) { clearTimeout(bgSleepRef.current); bgSleepRef.current = null; } setBgSleepAt(null); setBgSleepMin(0); };
  const stopBg = () => { plQueue.current = []; try { bcRef.current?.stopBackgroundMusic?.(); } catch (e) {} setBgPlaying(null); pushNowPlaying(""); clearBgSleep(); };
  const setBgSleep = (min) => {
    if (bgSleepRef.current) { clearTimeout(bgSleepRef.current); bgSleepRef.current = null; }
    setBgSleepMin(min);
    if (!min) { setBgSleepAt(null); toast("Sleep timer off"); return; }
    setBgSleepAt(Date.now() + min * 60000);
    bgSleepRef.current = setTimeout(() => { stopBg(); toast.success("Background music stopped (sleep timer)"); }, min * 60000);
    toast.success(`Background music will stop in ${min} min`);
  };
  const changeBgLevel = (v) => { setBgLevel(v); if (bcRef.current?.setMusicLevel) bcRef.current.setMusicLevel(v / 100); };
  const toggleBgOnAir = () => { const n = !bgOnAir; setBgOnAir(n); if (bcRef.current?.setMusicOnAir) bcRef.current.setMusicOnAir(n); toast.message(n ? "Music is ON AIR (listeners hear it)" : "Music is studio-only"); };
  const toggleBgDuck = () => { const n = !bgDuck; setBgDuck(n); if (bcRef.current?.setMusicDuck) bcRef.current.setMusicDuck(n); };
  const togglePreset = async (id) => {
    const next = bgPresetIds.includes(id) ? bgPresetIds.filter((x) => x !== id) : [...bgPresetIds, id];
    setBgPresetIds(next);
    try { await api.post("/live/bg-presets", { station_ids: next }); } catch (e) { toast.error("Could not save presets"); }
  };
  // ---- Live jingles (fire a station ID / sweeper on air) ----
  const [liveJingles, setLiveJingles] = useState([]);
  const loadLiveJingles = () => api.get("/jingles/host").then((r) => setLiveJingles(r.data || [])).catch(() => {});
  useEffect(() => { if (live) { loadLiveJingles(); loadShoutHistory(); loadCotd(); loadCotdSettings(); } }, [live]);
  const fireJingle = (j) => {
    if (!bcRef.current?.playBackgroundMusic) { toast.error("Go live first to fire a jingle"); return; }
    const url = `${api.defaults.baseURL}/jingles/${j.id}/audio`;
    const ok = bcRef.current.playBackgroundMusic(url, { loop: false, onEnded: () => setBgPlaying(null) });
    if (!ok) { toast.error("Could not play that jingle"); return; }
    bcRef.current.setMusicLevel(bgLevel / 100);
    bcRef.current.setMusicOnAir(true);
    setBgPlaying({ url, label: `Jingle: ${j.title}` });
    pushNowPlaying(j.title, "jingle");
    api.post(`/jingles/${j.id}/played`).catch(() => {});
    toast.success(`On air: ${j.title}`);
  };
  const [shoutName, setShoutName] = useState("");
  const [shoutMsg, setShoutMsg] = useState("");
  const [shoutBusy, setShoutBusy] = useState(false);
  const [shoutHistory, setShoutHistory] = useState([]);
  const [cotd, setCotd] = useState(null);
  const [cotdAuto, setCotdAuto] = useState(false);
  const loadShoutHistory = () => api.get("/jingles/shoutouts").then((r) => setShoutHistory(r.data || [])).catch(() => {});
  const loadCotd = () => api.get("/jingles/cotd").then((r) => setCotd(r.data || null)).catch(() => {});
  const loadCotdSettings = () => api.get("/jingles/cotd-settings").then((r) => setCotdAuto(!!r.data?.auto_reset)).catch(() => {});
  const toggleCotdAuto = async () => {
    const next = !cotdAuto; setCotdAuto(next);
    try { await api.post("/jingles/cotd-settings", { auto_reset: next }); toast.success(next ? "Caller of the Day will clear each morning" : "Auto-reset turned off"); }
    catch (e) { setCotdAuto(!next); toast.error("Could not update"); }
  };
  const fireShoutout = async () => {
    if (!shoutName.trim()) { toast.error("Enter a listener's name"); return; }
    if (!bcRef.current?.playBackgroundMusic) { toast.error("Go live first to send a shout-out"); return; }
    setShoutBusy(true);
    try {
      const { data } = await api.post("/jingles/shoutout", { name: shoutName.trim(), message: shoutMsg.trim() });
      const url = `${api.defaults.baseURL}/jingles/${data.id}/audio`;
      const ok = bcRef.current.playBackgroundMusic(url, { loop: false, onEnded: () => setBgPlaying(null) });
      if (!ok) { toast.error("Could not play the shout-out"); return; }
      bcRef.current.setMusicLevel(bgLevel / 100);
      bcRef.current.setMusicOnAir(true);
      setBgPlaying({ url, label: `Shout-out: ${shoutName.trim()}` });
      pushNowPlaying(`Shout-out: ${shoutName.trim()}`, "jingle");
      setShoutName(""); setShoutMsg("");
      loadShoutHistory();
      toast.success("Shout-out on air!");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the shout-out"); }
    finally { setShoutBusy(false); }
  };
  const replayShoutout = (s) => {
    if (!bcRef.current?.playBackgroundMusic) { toast.error("Go live first"); return; }
    const url = `${api.defaults.baseURL}/jingles/${s.id}/audio`;
    const ok = bcRef.current.playBackgroundMusic(url, { loop: false, onEnded: () => setBgPlaying(null) });
    if (!ok) { toast.error("Could not play that shout-out"); return; }
    bcRef.current.setMusicLevel(bgLevel / 100);
    bcRef.current.setMusicOnAir(true);
    setBgPlaying({ url, label: s.title });
    pushNowPlaying(s.title, "jingle");
    api.post(`/jingles/${s.id}/played`).catch(() => {});
    toast.success(`On air: ${s.title.replace("Shout-out: ", "")}`);
  };
  const favShoutout = async (s) => {
    try {
      const { data } = await api.post(`/jingles/shoutouts/${s.id}/favorite`);
      setShoutHistory((xs) => xs.map((x) => (x.id === data.id ? data : x)).sort((a, b) => (a.favorite === b.favorite ? 0 : a.favorite ? -1 : 1)));
    } catch (e) {}
  };
  const delShoutout = async (id) => {
    try { await api.delete(`/jingles/shoutouts/${id}`); setShoutHistory((xs) => xs.filter((x) => x.id !== id)); if (cotd?.id === id) setCotd(null); }
    catch (e) { toast.error("Could not delete"); }
  };
  const toggleCotd = async (s) => {
    try {
      const { data } = await api.post(`/jingles/shoutouts/${s.id}/cotd`);
      setCotd(data.cotd ? data : null);
      loadShoutHistory();
      toast.success(data.cotd ? `👑 ${s.title.replace("Shout-out: ", "")} is Caller of the Day` : "Caller of the Day cleared");
    } catch (e) { toast.error("Could not set Caller of the Day"); }
  };
  const shoutFormRef = useRef(null);
  const prefillShoutout = (c) => {
    setShoutName(c.caller_name || c.from_display || "");
    setShoutMsg(c.caller_city ? `from ${c.caller_city}` : "");
    toast.success("Shout-out ready — tap 'Shout it out'");
    setTimeout(() => { try { shoutFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); shoutFormRef.current?.querySelector("input")?.focus(); } catch (e) {} }, 60);
  };
  const toggleFavJingle = async (j) => {
    try { await api.post(`/jingles/${j.id}/favorite`); loadLiveJingles(); }
    catch (e) { toast.error("Could not update"); }
  };
  // ---- Sermon playlists ----
  const [bgPlaylists, setBgPlaylists] = useState([]);
  const [plBuild, setPlBuild] = useState(false);
  const [plName, setPlName] = useState("");
  const [plSelected, setPlSelected] = useState([]);
  const [plEditId, setPlEditId] = useState(null);
  const [plShuffle, setPlShuffle] = useState(false);
  const plDragIdx = useRef(null);
  const plQueue = useRef([]);
  const plIdx = useRef(0);
  const loadPlaylists = () => api.get("/live/playlists").then((r) => setBgPlaylists(r.data || [])).catch(() => {});
  useEffect(() => { if (live) loadPlaylists(); }, [live]);
  const togglePlItem = (id) => setPlSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const reorderPl = (from, to) => setPlSelected((prev) => { if (from == null || to == null || from === to) return prev; const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
  const openNewPlaylist = () => { setPlBuild(true); setPlEditId(null); setPlSelected([]); setPlName(""); };
  const editPlaylist = (pl) => { setPlBuild(true); setPlEditId(pl.id); setPlName(pl.name); setPlSelected(pl.item_ids || []); };
  const savePlaylist = async () => {
    if (!plSelected.length) { toast.error("Pick at least one item"); return; }
    try { await api.post("/live/playlists", { id: plEditId || undefined, name: plName.trim() || "Sermon set", item_ids: plSelected, kind: "sermon" }); setPlBuild(false); setPlEditId(null); setPlName(""); setPlSelected([]); toast.success("Playlist saved"); loadPlaylists(); }
    catch (e) { toast.error("Could not save playlist"); }
  };
  const deletePlaylist = async (id) => { try { await api.delete(`/live/playlists/${id}`); toast.success("Playlist removed"); loadPlaylists(); } catch (e) { toast.error("Could not remove"); } };
  const playQueueItem = () => {
    const it = plQueue.current[plIdx.current];
    if (!it || !bcRef.current?.playBackgroundMusic) return;
    bcRef.current.playBackgroundMusic(`${api.defaults.baseURL}${it.audio_url}`, { loop: false, onEnded: advancePlaylist });
    bcRef.current.setMusicLevel(bgLevel / 100); bcRef.current.setMusicOnAir(bgOnAir); bcRef.current.setMusicDuck(bgDuck); applyDuckDepths(duckTalk, duckCaller);
    setBgPlaying({ url: "playlist", label: `${it.title} (${plIdx.current + 1}/${plQueue.current.length})` });
    pushNowPlaying(it.title, it.kind || "sermon");
  };
  const advancePlaylist = () => {
    plIdx.current += 1;
    if (plIdx.current < plQueue.current.length) playQueueItem();
    else { plQueue.current = []; setBgPlaying(null); pushNowPlaying(""); toast.success("Playlist finished"); }
  };
  const playPlaylist = (pl) => {
    if (!bcRef.current?.playBackgroundMusic) { toast.error("Go live first"); return; }
    if (!pl.items?.length) { toast.error("This playlist is empty"); return; }
    const items = plShuffle ? [...pl.items].sort(() => Math.random() - 0.5) : pl.items;
    plQueue.current = items; plIdx.current = 0; playQueueItem();
    toast.success(`Playing: ${pl.name}${plShuffle ? " (shuffled)" : ""}`);
  };

  useEffect(() => { api.get("/voice/config").then((r) => setVoiceCfg(r.data)).catch(() => {}); }, []);
  const pollPhoneCallers = () => api.get("/voice/callers").then((r) => {
    let callers = r.data.callers || [];
    const sids = callers.map((c) => c.call_sid);
    const prev = prevCallerSidsRef.current;
    const newOnes = sids.filter((s) => !prev.includes(s));
    if (prev.length && newOnes.length) {
      if (chimeOnRef.current) playChime();
      toast.message(newOnes.length === 1 ? "New caller on the line" : `${newOnes.length} new callers on the line`);
      setFlashSids(newOnes);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashSids([]), 4500);
    }
    prevCallerSidsRef.current = sids;
    const since = onAirSinceRef.current;
    callers.forEach((c) => { if (c.on_air && !since[c.call_sid]) since[c.call_sid] = Date.now(); if (!c.on_air) delete since[c.call_sid]; });
    Object.keys(since).forEach((k) => { if (!sids.includes(k)) delete since[k]; });
    const waits = waitSinceRef.current;
    callers.forEach((c) => { if (!c.on_air && !waits[c.call_sid]) waits[c.call_sid] = Date.now(); if (c.on_air) delete waits[c.call_sid]; });
    Object.keys(waits).forEach((k) => { if (!sids.includes(k)) delete waits[k]; });
    const screen = screenSinceRef.current;
    callers.forEach((c) => { if (c.state === "screening" && !screen[c.call_sid]) screen[c.call_sid] = Date.now(); if (c.state !== "screening") delete screen[c.call_sid]; });
    Object.keys(screen).forEach((k) => { if (!sids.includes(k)) delete screen[k]; });
    const order = callerOrderRef.current;
    callers.sort((a, b) => { const ia = order.indexOf(a.call_sid), ib = order.indexOf(b.call_sid); return (ia < 0 ? 9999 : ia) - (ib < 0 ? 9999 : ib); });
    callerOrderRef.current = callers.map((c) => c.call_sid);
    setPhoneCallers(callers);
    try { bcRef.current?.setMusicCallerDuck?.(callers.some((c) => c.on_air)); } catch (e) {}
  }).catch(() => {});
  const loadMissed = () => api.get("/voice/missed-callers").then((r) => setMissedCallers(r.data.missed || [])).catch(() => {});
  const clearMissed = async () => { try { await api.delete("/voice/missed-callers"); setMissedCallers([]); toast.success("Missed calls cleared"); } catch (e) {} };
  const loadAutoDrop = () => api.get("/voice/auto-drop").then((r) => setAutoDrop(r.data || { enabled: true, minutes: 10, action: "sms_drop" })).catch(() => {});
  const saveAutoDrop = (patch) => { setAutoDrop((prev) => { const next = { ...prev, ...patch }; api.post("/voice/auto-drop", next).catch(() => {}); return next; }); };
  const queuePollRef = useRef(null);
  // Poll the waiting-caller queue whenever the line is configured — the host sees & screens
  // callers even before going live or connecting their own audio line.
  useEffect(() => {
    if (!voiceCfg?.configured) { setPhoneCallers([]); prevCallerSidsRef.current = []; return; }
    pollPhoneCallers(); loadMissed(); loadAutoDrop(); loadHoldLibrary();
    queuePollRef.current = setInterval(() => { pollPhoneCallers(); loadMissed(); }, 4000);
    return () => { clearInterval(queuePollRef.current); queuePollRef.current = null; };
  }, [voiceCfg?.configured]);
  const [autoConnectLine, setAutoConnectLine] = useState(() => { try { return localStorage.getItem("autoConnectLine") !== "0"; } catch (e) { return true; } });
  useEffect(() => { try { localStorage.setItem("autoConnectLine", autoConnectLine ? "1" : "0"); } catch (e) {} }, [autoConnectLine]);
  const autoConnectedRef = useRef(false);
  // Auto-connect the call-in line the moment the host goes live (so callers are one tap from air).
  useEffect(() => {
    if (!live) { autoConnectedRef.current = false; return; }
    if (autoConnectLine && voiceCfg?.configured && !callInJoined && !autoConnectedRef.current) {
      autoConnectedRef.current = true;
      setTimeout(() => { if (bcRef.current && !voiceCallRef.current) joinCallIn(); }, 1500);
    }
  }, [live, voiceCfg?.configured, autoConnectLine, callInJoined]);
  // Headset-only mute of hold music: while the toggle is on and nobody is on air, silence the
  // call-in line in the host's headphones (listeners/broadcast are unaffected). Auto-unmutes
  // the moment a caller goes on air so the host always hears the person they're talking to.
  useEffect(() => {
    if (!callInJoined || !bcRef.current) return;
    const anyOnAir = phoneCallers.some((c) => c.on_air);
    const anyActive = phoneCallers.some((c) => c.on_air || c.state === "screening");
    // Host should hear on-air AND screening (private) callers; only mute when nobody's live.
    const mute = muteHoldInHeadset && !anyActive;
    bcRef.current.setCallersMonitor?.(mute ? 0 : 1);
    bcRef.current.setPhoneRemoteEnabled?.(!mute);
    // Callers reach the LISTENER broadcast only when on air. A "screening" caller is heard by
    // the host but kept off the broadcast (private off-air chat).
    bcRef.current.setCallersBroadcast?.(anyOnAir ? 1 : 0);
  }, [muteHoldInHeadset, callInJoined, phoneCallers]);
  const joinCallIn = async () => {
    if (!bcRef.current) { toast.error("Go live first"); return; }
    setCallInBusy(true);
    try {
      const { data } = await api.post("/voice/token");
      const device = new Device(data.token, { codecPreferences: ["opus", "pcmu"] });
      voiceDeviceRef.current = device;
      device.on("tokenWillExpire", async () => { try { const r = await api.post("/voice/token"); device.updateToken(r.data.token); } catch (e) {} });
      device.on("error", (e) => { console.error("Twilio device error", e); toast.error("Call-in line error"); });
      await device.register();
      // Inject the on-air music bed into the caller's audio so they hear the show under the host's voice.
      try {
        const musicStream = bcRef.current?.getConfMusicStream?.();
        if (musicStream && device.audio?.addProcessor) {
          const processor = {
            _ctx: null, _mic: null, _music: null, _dest: null,
            async createProcessedStream(stream) {
              const AC = window.AudioContext || window.webkitAudioContext;
              const actx = new AC();
              try { await actx.resume(); } catch (e) {}
              this._ctx = actx;
              this._mic = actx.createMediaStreamSource(stream);
              this._music = actx.createMediaStreamSource(musicStream);
              this._dest = actx.createMediaStreamDestination();
              this._mic.connect(this._dest);
              this._music.connect(this._dest);
              return this._dest.stream;
            },
            async destroyProcessedStream() {
              try { this._mic?.disconnect(); this._music?.disconnect(); this._dest?.disconnect(); await this._ctx?.close(); } catch (e) {}
              this._ctx = this._mic = this._music = this._dest = null;
            },
          };
          callProcessorRef.current = processor;
          await device.audio.addProcessor(processor);
        }
      } catch (e) { console.warn("music-to-caller mix unavailable:", e); }
      const call = await device.connect({ params: { conference: "praisefm-live" } });
      voiceCallRef.current = call;
      call.on("accept", () => {
        try { const stream = call.getRemoteStream(); if (stream && bcRef.current?.addPhoneStream) bcRef.current.addPhoneStream(stream); } catch (e) {}
        callInJoinedRef.current = true;
        setCallInJoined(true);
        toast.success("Call-in line connected");
        if (bcRef.current?.setPhoneLevel) bcRef.current.setPhoneLevel(phoneLevel / 100);
        if (bcRef.current?.setCallerMusicLevel) bcRef.current.setCallerMusicLevel(callerMusicLevel / 100);
        pollPhoneCallers();
        phoneMeterRef.current = setInterval(() => { if (bcRef.current?.getPhoneLevel) setPhoneMeter(bcRef.current.getPhoneLevel()); }, 150);
        dumpPollRef.current = setInterval(async () => {
          try {
            const { data } = await api.get("/voice/dump-signal");
            if (data.at && data.at !== lastDumpAtRef.current) {
              const first = lastDumpAtRef.current === null;
              lastDumpAtRef.current = data.at;
              if (!first && bcRef.current?.dumpBroadcast) { bcRef.current.dumpBroadcast(); toast.error(`Flagged word "${data.word}" — auto-dumped from air`); loadDumpLog(); }
            }
          } catch (e) {}
        }, 1200);
      });
      call.on("disconnect", () => leaveCallIn());
      call.on("error", (e) => { console.error("Twilio call error", e); });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not connect call-in line");
    } finally { setCallInBusy(false); }
  };
  const leaveCallIn = () => {
    clearInterval(phonePollRef.current); phonePollRef.current = null;
    clearInterval(phoneMeterRef.current); phoneMeterRef.current = null;
    clearInterval(dumpPollRef.current); dumpPollRef.current = null;
    setPhoneMeter(0);
    try { voiceCallRef.current?.disconnect(); } catch (e) {}
    try { if (callProcessorRef.current && voiceDeviceRef.current?.audio?.removeProcessor) voiceDeviceRef.current.audio.removeProcessor(callProcessorRef.current); } catch (e) {}
    callProcessorRef.current = null;
    try { voiceDeviceRef.current?.destroy(); } catch (e) {}
    try { bcRef.current?.removePhoneStream?.(); } catch (e) {}
    voiceCallRef.current = null; voiceDeviceRef.current = null;
    callInJoinedRef.current = false;
    setCallInJoined(false);
  };
  const putOnAir = async (sid) => {
    // Use refs (never stale) so a connected line can't be blocked by a laggy state read.
    if (!callInJoinedRef.current && !voiceCallRef.current) { toast.error("Connect the call-in line first to bring callers on air"); return; }
    try { await api.post(`/voice/callers/${sid}/onair`); toast.success("Caller is on air"); confirmOnAir(sid); pollPhoneCallers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not put caller on air"); }
  };
  const takeOffAir = async (sid) => {
    try { await api.post(`/voice/callers/${sid}/offair`); toast.message("Off air — you can still talk privately (listeners don't hear them)"); pollPhoneCallers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not take caller off air"); }
  };
  const airNextCaller = () => { const next = phoneCallers.find((c) => !c.on_air); if (!next) { toast.message("No callers waiting"); return; } putOnAir(next.call_sid); };
  // Spacebar airs the NEXT UP caller instantly (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "Space" && e.key !== " ") return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (!callInJoined || !phoneCallers.some((c) => !c.on_air)) return;
      e.preventDefault();
      airNextCaller();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [callInJoined, phoneCallers]);
  const holdCaller = async (sid) => { try { await api.post(`/voice/callers/${sid}/hold`); toast.success("Caller back on hold"); pollPhoneCallers(); } catch (e) { toast.error("Could not hold caller"); } };
  const dropCaller = async (sid) => { try { await api.delete(`/voice/callers/${sid}`); toast.success("Call dropped"); pollPhoneCallers(); } catch (e) { toast.error("Could not drop call"); } };
  useEffect(() => () => leaveCallIn(), []);
  const doRevert = () => { if (revertSettingsRef.current) { applySoundSettings(revertSettingsRef.current); toast.success("Restored your setup"); revertSettingsRef.current = null; revertShowRef.current = ""; setRevertAvailable(false); } };
  const sinkSupported = typeof window !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
  const changeHpLevel = (v) => { setHpLevelState(v); hpLevelRef.current = v; if (v > 0) setFbTriggered(false); if (bcRef.current?.setMonitorLevel) bcRef.current.setMonitorLevel(v / 100); persistMemory({ hp_level: v }); };
  const changeFeedbackGuard = (v) => { setFeedbackGuard(v); feedbackGuardRef.current = v; if (v) fbCountRef.current = 0; };
  const refreshDevices = async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      setOutputDevices(devs.filter((d) => d.kind === "audiooutput"));
      setInputDevices(devs.filter((d) => d.kind === "audioinput"));
    } catch (e) {}
  };
  // Device labels are hidden until the browser has been granted mic access once.
  const unlockDevices = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      await refreshDevices();
    } catch (e) { toast.error("Microphone permission needed to list devices"); }
  };
  useEffect(() => { refreshDevices(); }, []);
  const applySink = async (id) => {
    setOutputDeviceId(id);
    persistMemory({ output_device_id: id });
    const el = monitorAudioRef.current;
    if (el && el.setSinkId) { try { await el.setSinkId(id); toast.success("Monitor output switched"); } catch (e) { toast.error("Could not switch output device"); } }
  };
  const playTestTone = async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      try { await ctx.resume(); } catch (e) {}
      const dest = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 523.25;
      gain.gain.value = 0.0001;
      osc.connect(gain); gain.connect(dest);
      const el = new Audio();
      el.srcObject = dest.stream;
      if (outputDeviceId && el.setSinkId) { try { await el.setSinkId(outputDeviceId); } catch (e) {} }
      await el.play().catch(() => {});
      const t = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.32, t + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
      osc.start(t); osc.stop(t + 0.95);
      osc.onended = () => { try { el.pause(); el.srcObject = null; ctx.close(); } catch (e) {} };
      toast.success("Playing test tone…");
    } catch (e) { toast.error("Could not play test tone"); }
  };
  const previewIntervalRef = useRef(null);
  const previewCtxRef = useRef(null);
  const previewStreamRef = useRef(null);
  const [previewMeter, setPreviewMeter] = useState(0);
  const [previewOn, setPreviewOn] = useState(false);
  const stopMicPreview = () => {
    clearInterval(previewIntervalRef.current); previewIntervalRef.current = null;
    try { previewStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) {}
    previewStreamRef.current = null;
    try { previewCtxRef.current?.close(); } catch (e) {}
    previewCtxRef.current = null;
    previewMeterRef.current = 0;
    setPreviewMeter(0); setPreviewOn(false);
  };
  const startMicPreview = async (id = micDeviceId) => {
    try {
      stopMicPreview();
      const s = await navigator.mediaDevices.getUserMedia({ audio: id ? { deviceId: { exact: id } } : true });
      previewStreamRef.current = s;
      const AC = window.AudioContext || window.webkitAudioContext;
      const pctx = new AC(); previewCtxRef.current = pctx;
      const src = pctx.createMediaStreamSource(s);
      const an = pctx.createAnalyser(); an.fftSize = 256; src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      setPreviewOn(true);
      refreshDevices();
      previewIntervalRef.current = setInterval(() => {
        an.getByteTimeDomainData(buf);
        let p = 0; for (let i = 0; i < buf.length; i++) { const d = Math.abs(buf[i] - 128); if (d > p) p = d; }
        const pk = Math.min(1, p / 128);
        previewMeterRef.current = pk;
        setPreviewMeter(pk);
      }, 100);
    } catch (e) { toast.error("Could not access microphone"); setPreviewOn(false); }
  };
  const changeMic = async (id) => {
    setMicDeviceId(id);
    persistMemory({ mic_device_id: id });
    if (live && bcRef.current?.switchMic) {
      try {
        const ns = await navigator.mediaDevices.getUserMedia({ audio: id ? { deviceId: { exact: id } } : true });
        bcRef.current.switchMic(ns);
        streamRef.current = ns;
        toast.success("Microphone switched — still live");
      } catch (e) { toast.error("Could not switch microphone"); }
    } else if (previewOn) {
      startMicPreview(id);
    }
  };
  useEffect(() => () => stopMicPreview(), []);
  const [autoDuck, setAutoDuckState] = useState(true);
  const [autoLevel, setAutoLevelState] = useState(true);
  const [targetLoudness, setTargetLoudnessState] = useState("broadcast");
  const [callerAutoLevel, setCallerAutoLevel] = useState({});
  const [callerMusic, setCallerMusic] = useState({});
  const [loudness, setLoudness] = useState(-70);
  const loudnessRef = useRef(-70);
  const LUFS_TARGET = { quiet: -20, broadcast: -16, loud: -12 };
  const [duckDepth, setDuckDepthState] = useState("medium");
  const [ducking, setDucking] = useState(false);
  const DUCK_FLOOR = { subtle: 0.7, medium: 0.45, strong: 0.2 };
  const [peakRecap, setPeakRecap] = useState(null);
  const showStartRef = useRef(0);
  const peaksRef = useRef([]);
  const lastPeakRef = useRef(0);
  const loudHistRef = useRef([]);
  const lastLoudSampleRef = useRef(0);
  const mixMemoryRef = useRef({ mic_level: 100, caller_levels: {} });
  const pendingMemRef = useRef({ caller_levels: {} });
  const memSaveRef = useRef(null);
  const appliedCallersRef = useRef(new Set());
  const [presets, setPresets] = useState([]);
  const persistMemory = (patch) => {
    if (patch.mic_level != null) { mixMemoryRef.current.mic_level = patch.mic_level; pendingMemRef.current.mic_level = patch.mic_level; }
    if (patch.caller_levels) {
      mixMemoryRef.current.caller_levels = { ...mixMemoryRef.current.caller_levels, ...patch.caller_levels };
      pendingMemRef.current.caller_levels = { ...pendingMemRef.current.caller_levels, ...patch.caller_levels };
    }
    if (patch.caller_autolevel) {
      mixMemoryRef.current.caller_autolevel = { ...(mixMemoryRef.current.caller_autolevel || {}), ...patch.caller_autolevel };
      pendingMemRef.current.caller_autolevel = { ...(pendingMemRef.current.caller_autolevel || {}), ...patch.caller_autolevel };
    }
    if (patch.caller_music) {
      mixMemoryRef.current.caller_music = { ...(mixMemoryRef.current.caller_music || {}), ...patch.caller_music };
      pendingMemRef.current.caller_music = { ...(pendingMemRef.current.caller_music || {}), ...patch.caller_music };
    }
    ["auto_duck", "auto_level", "duck_depth", "target_loudness", "noise_gate", "gate_sensitivity", "hp_level", "mic_device_id", "output_device_id"].forEach((k) => {
      if (patch[k] !== undefined) { mixMemoryRef.current[k] = patch[k]; pendingMemRef.current[k] = patch[k]; }
    });
    clearTimeout(memSaveRef.current);
    memSaveRef.current = setTimeout(() => {
      const body = pendingMemRef.current;
      pendingMemRef.current = { caller_levels: {} };
      api.put("/live/mix-memory", body).catch(() => {});
    }, 600);
  };
  const changeMicLevel = (v) => { setMicLevelState(v); if (bcRef.current?.setMicLevel) bcRef.current.setMicLevel(v / 100); persistMemory({ mic_level: v }); };
  const changeAutoDuck = (v) => { setAutoDuckState(v); if (bcRef.current?.setAutoDuck) bcRef.current.setAutoDuck(v); persistMemory({ auto_duck: v }); };
  const changeAutoLevel = (v) => { setAutoLevelState(v); if (bcRef.current?.setAutoLevel) bcRef.current.setAutoLevel(v); setCallerAutoLevel({}); persistMemory({ auto_level: v }); };
  const changeTargetLoudness = (v) => { setTargetLoudnessState(v); if (bcRef.current?.setTargetLoudness) bcRef.current.setTargetLoudness(v); persistMemory({ target_loudness: v }); };
  const toggleCallerAutoLevel = (c) => {
    const on = !(callerAutoLevel[c.user_id] ?? autoLevel);
    setCallerAutoLevel((prev) => ({ ...prev, [c.user_id]: on }));
    if (bcRef.current?.setCallerAutoLevel) bcRef.current.setCallerAutoLevel(c.user_id, on);
    persistMemory({ caller_autolevel: { [c.user_id]: on } });
  };
  const toggleCallerMusic = (c) => {
    const on = !callerMusic[c.user_id];
    setCallerMusic((prev) => ({ ...prev, [c.user_id]: on }));
    setCallerAutoLevel((prev) => ({ ...prev, [c.user_id]: on ? false : autoLevel }));
    if (bcRef.current?.setCallerMusic) bcRef.current.setCallerMusic(c.user_id, on);
    persistMemory({ caller_music: { [c.user_id]: on }, caller_autolevel: { [c.user_id]: on ? false : autoLevel } });
    toast.success(on ? `${c.user_name} → Music mode (clean, gentle duck)` : `${c.user_name} → Talk mode`);
  };
  const changeDuckDepth = (v) => { setDuckDepthState(v); if (bcRef.current?.setDuckDepth) bcRef.current.setDuckDepth(DUCK_FLOOR[v]); persistMemory({ duck_depth: v }); };
  useEffect(() => {
    if (!live) { setMeter(0); setMonMeter(0); setDucking(false); setLoudness(-70); loudnessRef.current = -70; return; }
    const t = setInterval(() => {
      if (!bcRef.current) return;
      const lvl = bcRef.current.getLevel ? bcRef.current.getLevel() : 0;
      setMeter(lvl);
      let mon = 0;
      if (bcRef.current.getMonitorLevel) { mon = bcRef.current.getMonitorLevel(); setMonMeter(mon); }
      // Feedback guard: sustained clipping on both mic input and monitor output while
      // monitoring is on almost always means a speaker→mic feedback howl. Mute the monitor.
      if (feedbackGuardRef.current && hpLevelRef.current > 0 && lvl > 0.93 && mon > 0.85) {
        fbCountRef.current += 1;
        if (fbCountRef.current >= 12) {
          fbCountRef.current = 0;
          setHpLevelState(0); hpLevelRef.current = 0;
          if (bcRef.current.setMonitorLevel) bcRef.current.setMonitorLevel(0);
          setFbTriggered(true);
          toast.warning("Feedback detected — headphone monitor muted. Use headphones or lower the monitor.");
        }
      } else {
        fbCountRef.current = 0;
      }
      if (bcRef.current.isDucking) setDucking(bcRef.current.isDucking());
      if (bcRef.current.getLoudness) {
        const lu = bcRef.current.getLoudness();
        loudnessRef.current = loudnessRef.current <= -70 ? lu : loudnessRef.current * 0.82 + lu * 0.18;
        setLoudness(loudnessRef.current);
        const nowMs = Date.now();
        if (nowMs - lastLoudSampleRef.current > 3000 && loudnessRef.current > -60) {
          loudHistRef.current.push({ t: Math.round((nowMs - showStartRef.current) / 1000), lu: Math.round(loudnessRef.current) });
          lastLoudSampleRef.current = nowMs;
        }
      }
      const mix = bcRef.current.getMixLevel ? bcRef.current.getMixLevel() : lvl;
      if (mix > 0.9) {
        const now = Date.now();
        if (now - lastPeakRef.current > 2500) {
          peaksRef.current.push({ t: Math.max(0, Math.round((now - showStartRef.current) / 1000)), level: mix });
        } else if (peaksRef.current.length) {
          const last = peaksRef.current[peaksRef.current.length - 1];
          if (mix > last.level) last.level = mix;
        }
        lastPeakRef.current = now;
      }
    }, 100);
    return () => clearInterval(t);
  }, [live]);
  // Safety net: if the host leaves the page / closes the tab while live, end the
  // broadcast server-side so the site's red "On air" banners don't stay stuck on.
  useEffect(() => {
    if (!live) return;
    const endSession = () => {
      try {
        const token = localStorage.getItem("token");
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live/stop`, {
          method: "POST", keepalive: true, credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch (e) {}
    };
    window.addEventListener("pagehide", endSession);
    return () => { window.removeEventListener("pagehide", endSession); endSession(); };
  }, [live]);
  // Heartbeat so the server knows the host is still on air (auto-end backstop).
  useEffect(() => {
    if (!live || !liveSid) return;
    const hb = () => api.post("/live/heartbeat").catch(() => {});
    hb();
    const t = setInterval(hb, 20000);
    return () => clearInterval(t);
  }, [live, liveSid]);
  const [pendingRec, setPendingRec] = useState(null);

  const loadEpisodes = () => api.get("/episodes").then((r) => setEpisodes(r.data)).catch(() => {});
  const loadSchedules = () => api.get("/live/schedule").then((r) => setSchedules(r.data)).catch(() => {});
  const loadPresets = () => api.get("/live/mix-presets").then((r) => setPresets(r.data)).catch(() => {});
  useEffect(() => {
    loadEpisodes(); loadSchedules(); loadPresets(); loadSoundProfiles(true); loadInbox(true); loadDumpLog();
    api.get("/live/mix-memory").then((r) => { mixMemoryRef.current = r.data; if (r.data.mic_level != null) setMicLevelState(r.data.mic_level); if (r.data.auto_duck != null) setAutoDuckState(r.data.auto_duck); if (r.data.auto_level != null) setAutoLevelState(r.data.auto_level); if (r.data.duck_depth) setDuckDepthState(r.data.duck_depth); if (r.data.target_loudness) setTargetLoudnessState(r.data.target_loudness); if (r.data.noise_gate != null) setNoiseGateState(r.data.noise_gate); if (r.data.gate_sensitivity) setGateSensitivityState(r.data.gate_sensitivity); if (r.data.hp_level != null) { setHpLevelState(r.data.hp_level); hpLevelRef.current = r.data.hp_level; } if (r.data.caller_music_level != null) setCallerMusicLevelState(r.data.caller_music_level); if (r.data.phone_level != null) setPhoneLevelState(r.data.phone_level); if (r.data.mic_device_id) setMicDeviceId(r.data.mic_device_id); if (r.data.output_device_id) setOutputDeviceId(r.data.output_device_id); }).catch(() => {});
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    const profPoll = setInterval(() => loadSoundProfiles(), 60000);
    return () => { clearInterval(profPoll); if (bcRef.current) bcRef.current.stop(); if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [isAdmin]);

  // Auto-apply each caller's remembered level + auto-level when they come on air.
  useEffect(() => {
    if (!live || !bcRef.current) return;
    const levels = mixMemoryRef.current.caller_levels || {};
    const al = mixMemoryRef.current.caller_autolevel || {};
    const music = mixMemoryRef.current.caller_music || {};
    callins.filter((c) => c.status === "granted" || c.status === "live").forEach((c) => {
      if (appliedCallersRef.current.has(c.user_id)) return;
      appliedCallersRef.current.add(c.user_id);
      if (levels[c.user_id] != null) {
        bcRef.current.setCallerLevel(c.user_id, levels[c.user_id]);
        setCallerLevels((prev) => ({ ...prev, [c.user_id]: levels[c.user_id] }));
      }
      if (al[c.user_id] != null) {
        bcRef.current.setCallerAutoLevel(c.user_id, al[c.user_id]);
        setCallerAutoLevel((prev) => ({ ...prev, [c.user_id]: al[c.user_id] }));
      }
      if (music[c.user_id]) {
        bcRef.current.setCallerMusic(c.user_id, true);
        setCallerMusic((prev) => ({ ...prev, [c.user_id]: true }));
        setCallerAutoLevel((prev) => ({ ...prev, [c.user_id]: false }));
      }
    });
  }, [callins, live]);

  const goLive = async () => {
    try {
      stopMicPreview();
      setRevertAvailable(false);
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: micDeviceId ? { deviceId: { exact: micDeviceId } } : true });
      } catch (devErr) {
        // Saved mic may no longer exist (e.g. host switched machines) — fall back to default.
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (micDeviceId) { setMicDeviceId(""); toast.message("Saved microphone not found — using the default mic."); }
      }
      streamRef.current = stream;
      const { data } = await api.post("/live/start", { title });
      const studio = await createLiveStudio(data.id, stream);
      studio.setOnCount(setCount);
      bcRef.current = studio;
      appliedCallersRef.current = new Set();
      studio.setAutoDuck(autoDuck);
      studio.setAutoLevel(autoLevel);
      studio.setTargetLoudness(targetLoudness);
      studio.setDuckDepth(DUCK_FLOOR[duckDepth]);
      if (studio.setMonitorLevel) studio.setMonitorLevel(hpLevel / 100);
      if (studio.setNoiseGate) studio.setNoiseGate(noiseGate);
      if (studio.setGateSensitivity) studio.setGateSensitivity(gateSensitivity);
      hpLevelRef.current = hpLevel; feedbackGuardRef.current = feedbackGuard; fbCountRef.current = 0; setFbTriggered(false);
      // Play the headphone monitor through a dedicated element so the host can choose the output device.
      if (studio.getMonitorStream) {
        try {
          if (!monitorAudioRef.current) monitorAudioRef.current = new Audio();
          const el = monitorAudioRef.current;
          el.srcObject = studio.getMonitorStream();
          el.autoplay = true;
          await el.play().catch(() => {});
          if (outputDeviceId && el.setSinkId) el.setSinkId(outputDeviceId).catch(() => {});
        } catch (e) {}
      }
      refreshDevices();
      setCallerAutoLevel({});
      setCallerMusic({});
      showStartRef.current = Date.now();
      peaksRef.current = [];
      lastPeakRef.current = 0;
      loudHistRef.current = [];
      lastLoudSampleRef.current = 0;
      setPeakRecap(null);
      // Load the default preset if one is set, else snap the mic to the host's usual level.
      const def = presets.find((p) => p.is_default);
      if (def) {
        changeMicLevel(def.mic_level ?? 100);
        const levels = {};
        (def.callers || []).forEach((cl) => { levels[cl.user_id] = cl.level; });
        mixMemoryRef.current.caller_levels = { ...mixMemoryRef.current.caller_levels, ...levels };
        toast.success(`Loaded preset "${def.name}"`);
      } else {
        const savedMic = mixMemoryRef.current.mic_level;
        if (savedMic != null) { setMicLevelState(savedMic); studio.setMicLevel(savedMic / 100); }
      }
      // Optionally record the mixed broadcast (host mic + callers) to save as an episode
      if (recordOn && typeof window.MediaRecorder !== "undefined") {
        try {
          airedTracksRef.current = [];
          const mixed = studio.getStream();
          const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
          const rec = new MediaRecorder(mixed, mime ? { mimeType: mime } : undefined);
          chunksRef.current = [];
          rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
          rec.start(1000);
          recorderRef.current = rec;
          recordStartRef.current = Date.now();
          setRecording(true);
        } catch (err) { console.warn("Recording unavailable", err); }
      }
      setLiveSid(data.id); setLive(true);
      toast.success("You're live!");
      // Auto-apply a scheduled show's sound profile if one is starting around now.
      try {
        const nowMs = Date.now();
        const match = schedules
          .filter((s) => s.sound_profile_id && Math.abs(new Date(s.scheduled_at).getTime() - nowMs) < 2 * 60 * 60 * 1000)
          .sort((a, b) => Math.abs(new Date(a.scheduled_at).getTime() - nowMs) - Math.abs(new Date(b.scheduled_at).getTime() - nowMs))[0];
        if (match) {
          const prof = soundProfiles.find((p) => p.id === match.sound_profile_id);
          if (prof) { revertSettingsRef.current = currentSoundSettings(); revertShowRef.current = match.title; setRevertAvailable(true); applySoundProfile(prof); toast.message(`Applied "${prof.name}" for "${match.title}"`); }
        }
        // Per-show hold music: activate the scheduled show's chosen library track.
        const holdMatch = schedules
          .filter((s) => s.hold_track_id && Math.abs(new Date(s.scheduled_at).getTime() - nowMs) < 2 * 60 * 60 * 1000)
          .sort((a, b) => Math.abs(new Date(a.scheduled_at).getTime() - nowMs) - Math.abs(new Date(b.scheduled_at).getTime() - nowMs))[0];
        if (holdMatch) { try { await api.post(`/voice/hold-library/${holdMatch.hold_track_id}/activate`); loadHoldLibrary(); toast.message(`Hold music set for "${holdMatch.title}"`); } catch (e) {} }
      } catch (e) {}
    } catch (e) {
      toast.error(e.name === "NotAllowedError" ? "Microphone permission denied" : "Could not start broadcast");
    }
  };
  const stopLive = async () => {
    // Capture the recording, then let the host review/name/trim it before publishing
    const rec = recorderRef.current;
    let blob = null;
    if (rec && rec.state !== "inactive") {
      const stopped = new Promise((resolve) => { rec.onstop = resolve; });
      try { rec.stop(); await stopped; } catch (e) {}
      blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      recorderRef.current = null; chunksRef.current = []; setRecording(false);
    }
    if (bcRef.current) bcRef.current.stop();
    leaveCallIn();
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (monitorAudioRef.current) { try { monitorAudioRef.current.pause(); monitorAudioRef.current.srcObject = null; } catch (e) {} }
    await api.post("/live/stop").catch(() => {});
    setLive(false); setLiveSid(null); setCount(0); setCallins([]); setRoster([]);
    appliedCallersRef.current = new Set();
    const dur = showStartRef.current ? Math.round((Date.now() - showStartRef.current) / 1000) : 0;
    setPeakRecap({ duration: dur, moments: peaksRef.current.slice(), loudness: loudHistRef.current.slice(), target: LUFS_TARGET[targetLoudness] });
    toast.success("Broadcast ended");
    if (revertSettingsRef.current) {
      const snap = revertSettingsRef.current; const showName = revertShowRef.current;
      revertSettingsRef.current = null; revertShowRef.current = ""; setRevertAvailable(false);
      toast(`"${showName}" ended — restore your previous sound setup?`, {
        duration: 12000,
        action: { label: "Restore", onClick: () => { applySoundSettings(snap); toast.success("Previous setup restored"); } },
      });
    }
    if (blob && blob.size > 1000) {
      setPendingRec({ blob, defaultTitle: `${title} — ${fmtDate(new Date())}`, tracks: [...airedTracksRef.current] });
    }
  };

  // Poll call-in queue + roster while live
  useEffect(() => {
    if (!live || !liveSid) return;
    const poll = () => {
      api.get(`/live/${liveSid}/callin/list`).then((r) => setCallins(r.data)).catch(() => {});
      api.get(`/live/${liveSid}/roster`).then((r) => setRoster(r.data)).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [live, liveSid]);

  const approveCall = async (c) => { await api.post(`/live/${liveSid}/callin/${c.id}/approve`).catch(() => {}); toast.success(`${c.user_name} can now speak`); };
  const inviteCall = async (u) => { await api.post(`/live/${liveSid}/callin/invite`, { user_id: u.user_id, user_name: u.user_name }).catch(() => {}); toast.success(`Invited ${u.user_name} to speak`); };
  const toggleMuteCall = async (c) => {
    const muted = !c.muted;
    if (bcRef.current) bcRef.current.muteCaller(c.user_id, muted);
    await api.post(`/live/${liveSid}/callin/${c.id}/mute`, { muted }).catch(() => {});
  };
  const endCall = async (c) => {
    if (bcRef.current) bcRef.current.removeCaller(c.user_id);
    await api.post(`/live/${liveSid}/callin/${c.id}/end`).catch(() => {});
    toast.success(`${c.user_name} removed from the call`);
  };
  const setCallerLevel = (c, v) => {
    setCallerLevels((prev) => ({ ...prev, [c.user_id]: v }));
    if (bcRef.current) bcRef.current.setCallerLevel(c.user_id, v);
    persistMemory({ caller_levels: { [c.user_id]: v } });
  };

  const pendingCalls = callins.filter((c) => c.status === "requested");
  const liveCalls = callins.filter((c) => c.status === "granted" || c.status === "live");
  const activeUserIds = new Set(callins.map((c) => c.user_id));
  const inviteable = roster.filter((u) => !activeUserIds.has(u.user_id));

  const savePreset = async () => {
    const name = window.prompt("Name this mix preset (e.g. \"Sunday Service\")");
    if (!name || !name.trim()) return;
    const callers = liveCalls.map((c) => ({ user_id: c.user_id, user_name: c.user_name, level: callerLevels[c.user_id] ?? 1 }));
    try {
      await api.post("/live/mix-presets", { name: name.trim(), mic_level: micLevel, callers });
      toast.success(`Saved preset "${name.trim()}"`);
      loadPresets();
    } catch (e) { toast.error("Could not save preset"); }
  };
  const applyPreset = (p) => {
    changeMicLevel(p.mic_level ?? 100);
    (p.callers || []).forEach((cl) => {
      mixMemoryRef.current.caller_levels = { ...mixMemoryRef.current.caller_levels, [cl.user_id]: cl.level };
      if (liveCalls.some((c) => c.user_id === cl.user_id) && bcRef.current) {
        bcRef.current.setCallerLevel(cl.user_id, cl.level);
        setCallerLevels((prev) => ({ ...prev, [cl.user_id]: cl.level }));
      }
    });
    const levels = {};
    (p.callers || []).forEach((cl) => { levels[cl.user_id] = cl.level; });
    persistMemory({ mic_level: p.mic_level ?? 100, caller_levels: levels });
    toast.success(`Applied "${p.name}"`);
  };
  const deletePreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try { await api.delete(`/live/mix-presets/${p.id}`); toast.success("Preset deleted"); loadPresets(); }
    catch (e) { toast.error("Could not delete"); }
  };
  const renamePreset = async (p) => {
    const name = window.prompt("Rename preset", p.name);
    if (!name || !name.trim() || name.trim() === p.name) return;
    try { await api.put(`/live/mix-presets/${p.id}`, { name: name.trim(), mic_level: p.mic_level, callers: p.callers }); toast.success("Preset renamed"); loadPresets(); }
    catch (e) { toast.error("Could not rename"); }
  };
  const duplicatePreset = async (p) => {
    try { await api.post("/live/mix-presets", { name: `${p.name} (copy)`, mic_level: p.mic_level, callers: p.callers }); toast.success(`Copied "${p.name}"`); loadPresets(); }
    catch (e) { toast.error("Could not copy preset"); }
  };
  const toggleDefaultPreset = async (p) => {
    try {
      const { data } = await api.post(`/live/mix-presets/${p.id}/default`, { is_default: !p.is_default });
      setPresets(data);
      toast.success(!p.is_default ? `"${p.name}" loads automatically when you go live` : "Default cleared");
    } catch (e) { toast.error("Could not update default"); }
  };

  const schedCoverRef = useRef(null);
  const [schedCoverName, setSchedCoverName] = useState("");
  const createSchedule = async () => {
    if (!sched.title.trim() || !sched.scheduled_at) { toast.error("Title and time required"); return; }
    try {
      const { data } = await api.post("/live/schedule", { title: sched.title, description: sched.description, scheduled_at: new Date(sched.scheduled_at).toISOString(), reminder_minutes: Number(sched.reminder_minutes) || 10, sound_profile_id: sched.sound_profile_id || "", hold_track_id: sched.hold_track_id || "" });
      const cover = schedCoverRef.current?.files?.[0];
      if (cover && data?.id) {
        const fd = new FormData(); fd.append("file", cover);
        try { await api.post(`/live/schedule/${data.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (e) {}
      }
      toast.success("Live show scheduled");
      setSched({ title: "In His Image Gospel Show", scheduled_at: "", description: "", reminder_minutes: 10, sound_profile_id: "" });
      setSchedCoverName("");
      if (schedCoverRef.current) schedCoverRef.current.value = "";
      loadSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
  };
  const delSchedule = async (id) => { await api.delete(`/live/schedule/${id}`); toast.success("Removed"); loadSchedules(); };

  const uploadEpisode = async () => {
    if (!epTitle.trim() || !epFileRef.current?.files[0]) { toast.error("Title and audio required"); return; }
    setUploading(true);
    const form = new FormData();
    form.append("title", epTitle); form.append("description", epDesc);
    form.append("series", epSeries);
    form.append("file", epFileRef.current.files[0]);
    if (coverRef.current?.files[0]) form.append("cover", coverRef.current.files[0]);
    try {
      await api.post("/episodes", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Episode published");
      setEpTitle(""); setEpDesc(""); setEpSeries(""); if (epFileRef.current) epFileRef.current.value = ""; if (coverRef.current) coverRef.current.value = "";
      loadEpisodes();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };
  const delEpisode = async (id) => { await api.delete(`/episodes/${id}`); toast.success("Deleted"); loadEpisodes(); };
  const [editEp, setEditEp] = useState(null);
  const [coverBust, setCoverBust] = useState(0);
  const regenCover = async (id) => {
    try { await api.post(`/episodes/${id}/regenerate-cover`, {}); setCoverBust((v) => v + 1); toast.success("Fresh cover generated 🎨"); loadEpisodes(); }
    catch (e) { toast.error("Could not regenerate the cover"); }
  };
  const [savingEp, setSavingEp] = useState(false);
  const editCoverRef = useRef(null);
  const saveEp = async () => {
    if (!editEp.title.trim()) { toast.error("Title is required"); return; }
    setSavingEp(true);
    try {
      const fd = new FormData();
      fd.append("title", editEp.title);
      fd.append("description", editEp.description || "");
      fd.append("series", editEp.series || "");
      if (editEp.coverFile) fd.append("cover", editEp.coverFile);
      await api.put(`/episodes/${editEp.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Episode updated");
      setEditEp(null); loadEpisodes();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } finally { setSavingEp(false); }
  };
  const setRole = async (id, role) => { await api.put(`/users/${id}/role`, { role }); toast.success("Role updated"); api.get("/users").then((r) => setUsers(r.data)); };

  if (user && user.role === "user") {
    return <div className="px-4 sm:px-6 lg:px-12 py-20 text-center text-zinc-400" data-testid="host-denied">Host access required. Ask an admin to grant you host rights.</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10">
      <div className="flex items-center gap-3 mb-8 flex-wrap">
        <Mic className="w-6 h-6 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Host Studio</h1>
        {voiceCfg?.configured && voiceCfg?.number_display && (
          <a href={`tel:${voiceCfg.number}`} data-testid="dashboard-callin-number" className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#E6B34A]/40 bg-[#E6B34A]/10 px-4 py-2 hover:bg-[#E6B34A]/15 transition-colors">
            <PhoneCall className="w-4 h-4 text-[#E6B34A]" />
            <span className="text-[11px] uppercase tracking-wide text-zinc-400">Call-in</span>
            <span className="font-display font-extrabold text-[#E6B34A]">{voiceCfg.number_display}</span>
          </a>
        )}
      </div>

      <Tabs defaultValue="golive">
        <TabsList className="bg-[#141C42] border border-white/5 w-full max-w-full overflow-x-auto justify-start no-scrollbar">
          <TabsTrigger value="golive" data-testid="tab-golive" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Go Live</TabsTrigger>
          <TabsTrigger value="episodes" data-testid="tab-episodes" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Episodes</TabsTrigger>
          {isAdmin && <TabsTrigger value="hosts" data-testid="tab-hosts" className="data-[state=active]:bg-[#E6B34A] data-[state=active]:text-[#0A1030]">Hosts</TabsTrigger>}
          <button onClick={() => setShowSchedule(true)} data-testid="open-schedule" className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold text-zinc-300 hover:bg-white/10 transition-colors whitespace-nowrap shrink-0"><CalendarPlus className="w-4 h-4 text-[#E6B34A]" /> Schedule a show</button>
        </TabsList>

        <TabsContent value="golive" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#141C42] p-8">
              {live && liveSid && <FloatingReactions sid={liveSid} />}
              <label className="overline text-zinc-400">Show title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={live} data-testid="live-title" className="mt-1.5 mb-6 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              {!live ? (
                <>
                  {peakRecap && (
                    <div data-testid="peak-recap" className="mb-4 rounded-xl bg-[#0C1338] border border-white/5 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="overline text-[#E6B34A] flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Last show recap</span>
                        <button onClick={() => setPeakRecap(null)} data-testid="dismiss-recap" className="p-0.5 rounded-full hover:bg-white/10 text-zinc-500 hover:text-zinc-200"><X className="w-3.5 h-3.5" /></button>
                      </div>
                      {peakRecap.moments.length === 0 ? (
                        <p className="text-[13px] text-emerald-300">The mix stayed clean the whole show — nicely balanced. 👏</p>
                      ) : (
                        <>
                          <p className="text-[13px] text-zinc-300 mb-2">The mix ran hot {peakRecap.moments.length} time{peakRecap.moments.length === 1 ? "" : "s"}. Ease the mic or a caller down a touch at these points next time:</p>
                          <div className="flex flex-wrap gap-1.5" data-testid="peak-moments">
                            {peakRecap.moments.map((m, i) => (
                              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-[#FF3B30]/15 text-[#FF7A70] px-2.5 py-1 text-[11px] font-mono tabular-nums">
                                {String(Math.floor(m.t / 60)).padStart(2, "0")}:{String(m.t % 60).padStart(2, "0")} · {Math.round(m.level * 100)}%
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                      {peakRecap.loudness && peakRecap.loudness.length >= 2 && (() => {
                        const pts = peakRecap.loudness;
                        const W = 320, H = 60, lo = -40, hi = 0;
                        const maxT = pts[pts.length - 1].t || 1;
                        const x = (t) => (t / maxT) * W;
                        const y = (lu) => H - ((Math.max(lo, Math.min(hi, lu)) - lo) / (hi - lo)) * H;
                        const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.lu).toFixed(1)}`).join(" ");
                        const ty = y(peakRecap.target);
                        return (
                          <div className="mt-3 pt-3 border-t border-white/5" data-testid="loudness-history">
                            <p className="overline text-[#E6B34A] mb-2 flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Loudness over the show</p>
                            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-14">
                              <line x1="0" y1={ty} x2={W} y2={ty} stroke="#E6B34A" strokeWidth="1" strokeDasharray="4 4" opacity="0.6" />
                              <path d={path} fill="none" stroke="#34D399" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                            </svg>
                            <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
                              <span>target {peakRecap.target} LUFS (dashed)</span>
                              <span>{Math.floor(peakRecap.duration / 60)}m {peakRecap.duration % 60}s show</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  <div className="mb-4 rounded-xl bg-[#0C1338] border border-white/5 p-4" data-testid="sound-check">
                    <div className="flex items-center justify-between mb-3">
                      <span className="overline text-[#E6B34A] flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Sound check</span>
                      <button onClick={unlockDevices} data-testid="detect-devices" className="text-xs font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors">Detect devices</button>
                    </div>
                    <label className="text-[11px] text-zinc-400 flex items-center gap-1.5 mb-1"><Mic className="w-3.5 h-3.5 text-[#E6B34A]" /> Microphone</label>
                    <select value={micDeviceId} onChange={(e) => changeMic(e.target.value)} data-testid="mic-device" className="w-full mb-2 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                      <option value="">Default microphone</option>
                      {inputDevices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>)}
                    </select>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-2 flex-1 rounded-full bg-white/5 overflow-hidden" data-testid="preview-meter">
                        <div className="h-full rounded-full transition-[width] duration-75" style={{ width: `${Math.round(previewMeter * 100)}%`, background: previewMeter > 0.85 ? "#FF3B30" : previewMeter > 0.6 ? "#E6B34A" : "#34D399" }} />
                      </div>
                      <button onClick={() => (previewOn ? stopMicPreview() : startMicPreview())} data-testid="mic-preview-toggle" className={`shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${previewOn ? "bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>{previewOn ? "Stop" : "Test mic"}</button>
                    </div>
                    {sinkSupported && (
                      <>
                        <label className="text-[11px] text-zinc-400 flex items-center gap-1.5 mb-1"><Headphones className="w-3.5 h-3.5 text-[#E6B34A]" /> Headphones / output</label>
                        <div className="flex items-center gap-2 mb-3">
                          <select value={outputDeviceId} onChange={(e) => applySink(e.target.value)} data-testid="output-device-pre" className="flex-1 min-w-0 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                            <option value="">System default output</option>
                            {outputDevices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Output ${i + 1}`}</option>)}
                          </select>
                        </div>
                      </>
                    )}
                    <button onClick={playTestTone} data-testid="test-tone" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E2A5A] border border-white/10 text-sm font-semibold text-zinc-200 hover:border-[#E6B34A] transition-colors">
                      <Volume2 className="w-4 h-4 text-[#E6B34A]" /> Play test tone
                    </button>
                    <p className="text-[11px] text-zinc-500 mt-2">Confirm you can hear the tone in the right headphones before you go live. Tap "Detect devices" to see device names.</p>
                    <button onClick={autoLearnLevel} disabled={learning} data-testid="auto-level-btn" className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E2A5A] border border-white/10 text-sm font-semibold text-zinc-200 hover:border-[#E6B34A] transition-colors disabled:opacity-60">
                      {learning ? <><Loader2 className="w-4 h-4 animate-spin" /> Listening… speak normally</> : <><Wand2 className="w-4 h-4 text-[#E6B34A]" /> Auto-set mic level</>}
                    </button>
                    <div className="mt-3 pt-3 border-t border-white/5" data-testid="sound-profiles">
                      {inbox.length > 0 && (
                        <div className="mb-3 rounded-xl border border-[#E6B34A]/30 bg-[#E6B34A]/5 p-3" data-testid="profile-inbox">
                          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold text-[#E6B34A]"><Inbox className="w-3.5 h-3.5" /> Shared with you</div>
                          <div className="space-y-2">
                            {inbox.map((p) => (
                              <div key={p.id} className="flex items-center gap-2" data-testid={`inbox-${p.id}`}>
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold truncate">{p.name}</p>
                                  {p.shared_by && <p className="text-[10px] text-zinc-500">from {p.shared_by}</p>}
                                </div>
                                <button onClick={() => setPreviewProfile(p)} data-testid={`inbox-preview-${p.id}`} title="Preview" className="p-1 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-[#E6B34A]"><Eye className="w-3.5 h-3.5" /></button>
                                <button onClick={() => acceptInbox(p)} data-testid={`inbox-accept-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34] transition-colors"><Check className="w-3 h-3" /> Add</button>
                                <button onClick={() => dismissInbox(p)} data-testid={`inbox-dismiss-${p.id}`} title="Dismiss" className="p-1 rounded-lg hover:bg-[#FF3B30]/15 text-zinc-500 hover:text-[#FF3B30]"><X className="w-3.5 h-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] text-zinc-400 flex items-center gap-1.5"><Save className="w-3.5 h-3.5 text-[#E6B34A]" /> Sound profiles</span>
                        <button onClick={saveSoundProfile} data-testid="save-profile" className="text-xs font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors">Save this setup</button>
                      </div>
                      {soundProfiles.length === 0 ? (
                        <p className="text-[11px] text-zinc-500">Save named setups like "Studio", "Home" or "Phone" and switch between them in one tap.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2" data-testid="profile-list">
                          {soundProfiles.map((p) => (
                            <span key={p.id} className={`inline-flex items-center gap-1 rounded-full border pl-2 pr-1.5 py-1 text-xs ${p.is_default ? "bg-[#E6B34A]/15 border-[#E6B34A]/40" : "bg-[#1E2A5A] border-white/10"}`} data-testid={`profile-${p.id}`}>
                              <button onClick={() => toggleDefaultProfile(p)} data-testid={`default-profile-${p.id}`} title={p.is_default ? "Default — loads when you open the studio" : "Set as default"} className={`p-0.5 rounded-full transition-colors ${p.is_default ? "text-[#E6B34A]" : "text-zinc-500 hover:text-[#E6B34A]"}`}><Star className="w-3 h-3" fill={p.is_default ? "currentColor" : "none"} /></button>
                              {renamingId === p.id ? (
                                <input autoFocus value={renameValue} maxLength={40} onChange={(e) => setRenameValue(e.target.value)} onBlur={() => commitRename(p)} onKeyDown={(e) => { if (e.key === "Enter") commitRename(p); if (e.key === "Escape") setRenamingId(null); }} data-testid={`rename-input-${p.id}`} className="bg-[#0A1030] border border-[#E6B34A]/50 rounded px-1.5 py-0.5 text-xs font-semibold text-zinc-100 focus:outline-none focus:ring-1 focus:ring-[#E6B34A] w-28" />
                              ) : (
                                <button onClick={() => applySoundProfile(p)} onDoubleClick={() => startRename(p)} data-testid={`apply-profile-${p.id}`} title="Tap to apply · double-tap to rename" className="font-semibold hover:text-[#E6B34A] transition-colors">{p.name}</button>
                              )}
                              {p.is_default && <span data-testid={`default-badge-${p.id}`} className="rounded-full bg-[#E6B34A] text-[#0A1030] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide">Default</span>}
                              {renamingId !== p.id && <button onClick={() => startRename(p)} data-testid={`rename-profile-${p.id}`} title="Rename this level" className="p-0.5 rounded-full hover:bg-white/10 text-zinc-500 hover:text-[#E6B34A]"><Pencil className="w-3 h-3" /></button>}
                              <button onClick={() => setPreviewProfile(p)} data-testid={`preview-profile-${p.id}`} title="Preview settings" className="p-0.5 rounded-full hover:bg-white/10 text-zinc-500 hover:text-[#E6B34A]"><Eye className="w-3 h-3" /></button>
                              <button onClick={() => shareSoundProfile(p)} data-testid={`share-profile-${p.id}`} title="Share with a co-host" className="p-0.5 rounded-full hover:bg-white/10 text-zinc-500 hover:text-[#E6B34A]"><UserPlus className="w-3 h-3" /></button>
                              <button onClick={() => deleteSoundProfile(p)} data-testid={`del-profile-${p.id}`} className="p-0.5 rounded-full hover:bg-[#FF3B30]/15 text-zinc-500 hover:text-[#FF3B30]"><X className="w-3 h-3" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={() => setRecordOn((v) => !v)} data-testid="record-toggle" className={`w-full mb-4 flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${recordOn ? "border-[#FF3B30]/50 bg-[#FF3B30]/10" : "border-white/10 bg-[#0C1338]"}`}>
                    <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${recordOn ? "bg-[#FF3B30]" : "bg-white/15"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${recordOn ? "left-[22px]" : "left-0.5"}`} /></span>
                    <span className="flex items-center gap-2 text-sm font-semibold">{recordOn && <span className="w-2 h-2 rounded-full bg-[#FF3B30] live-dot" />}Record this show &amp; save it to Episodes</span>
                    <span className={`ml-auto text-[11px] font-bold ${recordOn ? "text-[#FF7A70]" : "text-zinc-500"}`}>{recordOn ? "ON" : "OFF"}</span>
                  </button>
                  <button onClick={goLive} data-testid="go-live" className="w-full py-4 rounded-xl bg-[#FF3B30] text-white font-bold hover:bg-[#E22b21] transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#FF3B30]/20">
                    <span className="w-2.5 h-2.5 rounded-full bg-white shrink-0" /> OFF AIR — Go Live <Mic className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <div className="space-y-4 relative z-10">
                  <div className="flex items-center justify-between rounded-xl bg-emerald-500/10 border border-emerald-500/40 px-4 py-3" data-testid="onair-status">
                    <span className="flex items-center gap-2 text-emerald-400 font-bold"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 live-dot" /> LIVE ON AIR</span>
                    <span className="flex items-center gap-1.5 text-sm text-zinc-300" data-testid="listener-count"><Users className="w-4 h-4" /> {count} listening</span>
                  </div>
                  {recording && (
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#FF7A70]" data-testid="recording-indicator">
                      <span className="w-2 h-2 rounded-full bg-[#FF3B30] live-dot" /> Recording — this show will be saved to Episodes when you end it.
                    </div>
                  )}
                  <p className="text-sm text-zinc-400">Praise reactions from listeners float here in real time. 🙌</p>
                  <div className="rounded-xl bg-[#0C1338] border border-white/5 p-4" data-testid="sound-levels">
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline text-[#E6B34A] flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Microphone level</span>
                      <span className="text-xs text-zinc-400 tabular-nums">{micLevel}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-2" data-testid="level-meter">
                      <div className="h-full rounded-full transition-[width] duration-75" style={{ width: `${Math.round(meter * 100)}%`, background: meter > 0.85 ? "#FF3B30" : meter > 0.6 ? "#E6B34A" : "#34D399" }} />
                    </div>
                    {(() => {
                      const target = LUFS_TARGET[targetLoudness];
                      const lu = Math.round(loudness);
                      const onTarget = loudness > -60 && Math.abs(loudness - target) <= 3;
                      const low = loudness <= -60;
                      return (
                        <div className="flex items-center gap-2 mb-2 text-[11px]" data-testid="loudness-meter">
                          <Gauge className={`w-3.5 h-3.5 shrink-0 ${low ? "text-zinc-600" : onTarget ? "text-emerald-400" : "text-[#E6B34A]"}`} />
                          <span className={`font-mono tabular-nums font-bold ${low ? "text-zinc-600" : onTarget ? "text-emerald-300" : "text-[#E6B34A]"}`} data-testid="loudness-value">{low ? "—" : `${lu}`} LUFS</span>
                          <span className="text-zinc-500">· target {target} ({targetLoudness})</span>
                          {!low && <span className={`ml-auto font-semibold ${onTarget ? "text-emerald-300" : "text-zinc-400"}`}>{onTarget ? "On target" : loudness < target ? "A touch quiet" : "A touch loud"}</span>}
                        </div>
                      );
                    })()}
                    <input type="range" min={0} max={200} step={5} value={micLevel} onChange={(e) => changeMicLevel(Number(e.target.value))} data-testid="mic-level" className="w-full accent-[#E6B34A]" />
                    <p className="text-[11px] text-zinc-500 mt-1.5">Adjust your broadcast volume. Keep the peak comfortable — around 100–130% for a clear, warm sound.</p>
                    {inputDevices.length > 0 && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5" data-testid="live-mic-switch-row">
                        <Mic className="w-4 h-4 text-[#E6B34A] shrink-0" />
                        <select value={micDeviceId} onChange={(e) => changeMic(e.target.value)} data-testid="live-mic-device" className="flex-1 min-w-0 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                          <option value="">Default microphone</option>
                          {inputDevices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>)}
                        </select>
                        <span className="text-[10px] text-zinc-500 shrink-0">switch live</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5 flex-wrap" data-testid="mic-presets">
                      <span className="text-[11px] text-zinc-500">Quick level</span>
                      {MIC_PRESETS.map(([name, val]) => (
                        <button key={name} onClick={() => applyMicPreset(name, val)} data-testid={`mic-preset-${val}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${micLevel === val ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300 hover:bg-white/10"}`}>{name}</button>
                      ))}
                      <button onClick={autoLearnLevel} disabled={learning} data-testid="auto-level-live" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#1E2A5A] text-zinc-300 hover:bg-white/10 transition-colors disabled:opacity-60">{learning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 text-[#E6B34A]" />} Auto</button>
                    </div>
                    <label className="flex items-center gap-2 text-[13px] text-zinc-300 mt-3 cursor-pointer">
                      <input type="checkbox" checked={noiseGate} onChange={(e) => changeNoiseGate(e.target.checked)} data-testid="noise-gate-toggle" className="accent-[#E6B34A] w-4 h-4" />
                      <ArrowDownWideNarrow className="w-4 h-4 text-[#E6B34A]" />
                      Noise gate — mute my mic between sentences &amp; cut background hum
                    </label>
                    {noiseGate && (
                      <div className="flex items-center gap-2 mt-2 pl-6" data-testid="gate-sensitivity">
                        <span className="text-[11px] text-zinc-500">Closes</span>
                        <div className="flex gap-1">
                          {["soft", "medium", "strong"].map((s) => (
                            <button key={s} onClick={() => changeGateSensitivity(s)} data-testid={`gate-sens-${s}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize transition-colors ${gateSensitivity === s ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300 hover:bg-white/10"}`}>{s}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className="flex items-center gap-2 text-[13px] text-zinc-300 mt-3 pt-3 border-t border-white/5 cursor-pointer">
                      <input type="checkbox" checked={autoDuck} onChange={(e) => changeAutoDuck(e.target.checked)} data-testid="auto-duck-toggle" className="accent-[#E6B34A] w-4 h-4" />
                      <ArrowDownWideNarrow className="w-4 h-4 text-[#E6B34A]" />
                      Auto-duck my mic when a caller speaks
                      {ducking && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300" data-testid="ducking-indicator"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" /> DUCKING</span>}
                    </label>
                    {autoDuck && (
                      <div className="flex items-center gap-2 mt-2 pl-6" data-testid="duck-depth">
                        <span className="text-[11px] text-zinc-500">Dip strength</span>
                        <div className="flex gap-1">
                          {["subtle", "medium", "strong"].map((d) => (
                            <button key={d} onClick={() => changeDuckDepth(d)} data-testid={`duck-depth-${d}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize transition-colors ${duckDepth === d ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300 hover:bg-white/10"}`}>{d}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className="flex items-center gap-2 text-[13px] text-zinc-300 mt-2 cursor-pointer">
                      <input type="checkbox" checked={autoLevel} onChange={(e) => changeAutoLevel(e.target.checked)} data-testid="auto-level-toggle" className="accent-[#E6B34A] w-4 h-4" />
                      <Wand2 className="w-4 h-4 text-[#E6B34A]" />
                      Auto-level callers (even out quiet &amp; loud voices)
                    </label>
                    {autoLevel && (
                      <div className="flex items-center gap-2 mt-2 pl-6" data-testid="target-loudness">
                        <span className="text-[11px] text-zinc-500">Target</span>
                        <div className="flex gap-1">
                          {["quiet", "broadcast", "loud"].map((l) => (
                            <button key={l} onClick={() => changeTargetLoudness(l)} data-testid={`loudness-${l}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize transition-colors ${targetLoudness === l ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300 hover:bg-white/10"}`}>{l}</button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl bg-[#0C1338] border border-white/5 p-4" data-testid="headphone-levels">
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline text-[#E6B34A] flex items-center gap-1.5"><Headphones className="w-3.5 h-3.5" /> Headphone level</span>
                      <span className="text-xs text-zinc-400 tabular-nums">{hpLevel === 0 ? "Muted" : `${hpLevel}%`}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-2" data-testid="headphone-meter">
                      <div className="h-full rounded-full transition-[width] duration-75" style={{ width: `${Math.round(monMeter * 100)}%`, background: monMeter > 0.85 ? "#FF3B30" : monMeter > 0.6 ? "#E6B34A" : "#34D399" }} />
                    </div>
                    <input type="range" min={0} max={100} step={5} value={hpLevel} onChange={(e) => changeHpLevel(Number(e.target.value))} data-testid="headphone-level" className="w-full accent-[#E6B34A]" />
                    <p className="text-[11px] text-zinc-500 mt-1.5">Monitor your live mix (your mic + callers) in your headphones. Keep it at 0 if you're on speakers to avoid echo/feedback.</p>
                    {sinkSupported && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5" data-testid="output-device-row">
                        <Headphones className="w-4 h-4 text-[#E6B34A] shrink-0" />
                        <select value={outputDeviceId} onChange={(e) => applySink(e.target.value)} data-testid="output-device" className="flex-1 min-w-0 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                          <option value="">System default output</option>
                          {outputDevices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Output ${i + 1}`}</option>)}
                        </select>
                        <button onClick={refreshDevices} data-testid="refresh-outputs" title="Refresh device list" className="shrink-0 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors"><Radio className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                    <label className="flex items-center gap-2 text-[13px] text-zinc-300 mt-3 pt-3 border-t border-white/5 cursor-pointer">
                      <input type="checkbox" checked={feedbackGuard} onChange={(e) => changeFeedbackGuard(e.target.checked)} data-testid="feedback-guard-toggle" className="accent-[#E6B34A] w-4 h-4" />
                      <ShieldCheck className="w-4 h-4 text-[#E6B34A]" />
                      Feedback guard — auto-mute the monitor if it starts to howl
                    </label>
                    {fbTriggered && (
                      <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[#FF7A70]" data-testid="feedback-guard-alert">
                        <AlertTriangle className="w-3 h-3 shrink-0" /> Feedback detected — monitor muted. Switch to headphones, then raise the level again.
                      </div>
                    )}
                  </div>
                  {(soundProfiles.length > 0 || revertAvailable) && (
                    <div className="rounded-xl bg-[#0C1338] border border-white/5 p-4" data-testid="live-sound-profiles">
                      <div className="flex items-center justify-between mb-2">
                        <span className="overline text-[#E6B34A] flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Sound profiles</span>
                        {revertAvailable && (
                          <button onClick={doRevert} data-testid="revert-setup" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#E6B34A]/15 text-[#E6B34A] hover:bg-[#E6B34A]/25 transition-colors">
                            <ArrowDownWideNarrow className="w-3 h-3" /> Revert to my setup
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2" data-testid="live-profile-list">
                        {soundProfiles.map((p) => (
                          <button key={p.id} onClick={() => applySoundProfile(p)} data-testid={`live-apply-profile-${p.id}`} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${p.is_default ? "border-[#E6B34A]/50 bg-[#E6B34A]/15 text-[#E6B34A]" : "border-white/10 bg-[#1E2A5A] hover:border-[#E6B34A] hover:text-[#E6B34A]"}`}>
                            {p.is_default && <Star className="w-3 h-3 text-[#E6B34A]" fill="currentColor" />}{p.name}
                            {p.is_default && <span data-testid={`live-default-badge-${p.id}`} className="ml-0.5 rounded-full bg-[#E6B34A] text-[#0A1030] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide">Default</span>}
                          </button>
                        ))}
                      </div>
                      {soundProfiles.length > 0 && <p className="text-[11px] text-zinc-500 mt-2">Tap a profile to switch your whole sound setup mid-show.</p>}
                    </div>
                  )}
                  <div className="rounded-xl bg-[#0C1338] border border-white/5 p-4" data-testid="mix-presets">
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline text-[#E6B34A] flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Mix presets</span>
                      <button onClick={savePreset} data-testid="save-preset" className="text-xs font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors">Save current mix</button>
                    </div>
                    {presets.length === 0 ? (
                      <p className="text-[11px] text-zinc-500">Save your favourite mix so mic &amp; caller levels snap back next show. Levels you set are also remembered per caller automatically.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2" data-testid="preset-list">
                        {presets.map((p) => (
                          <span key={p.id} className={`inline-flex items-center gap-1 rounded-full border pl-2 pr-1.5 py-1 text-xs ${p.is_default ? "bg-[#E6B34A]/15 border-[#E6B34A]/40" : "bg-[#1E2A5A] border-white/10"}`} data-testid={`preset-${p.id}`}>
                            <button onClick={() => toggleDefaultPreset(p)} data-testid={`default-preset-${p.id}`} title={p.is_default ? "Default — loads when you go live" : "Set as default"} className={`p-0.5 rounded-full transition-colors ${p.is_default ? "text-[#E6B34A]" : "text-zinc-500 hover:text-[#E6B34A]"}`}><Star className="w-3 h-3" fill={p.is_default ? "currentColor" : "none"} /></button>
                            <button onClick={() => applyPreset(p)} data-testid={`apply-preset-${p.id}`} className="font-semibold hover:text-[#E6B34A] transition-colors">{p.name}</button>
                            <button onClick={() => renamePreset(p)} data-testid={`rename-preset-${p.id}`} className="p-0.5 rounded-full hover:bg-white/10 text-zinc-500 hover:text-zinc-200"><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => duplicatePreset(p)} data-testid={`dup-preset-${p.id}`} className="p-0.5 rounded-full hover:bg-white/10 text-zinc-500 hover:text-zinc-200"><Copy className="w-3 h-3" /></button>
                            <button onClick={() => deletePreset(p)} data-testid={`del-preset-${p.id}`} className="p-0.5 rounded-full hover:bg-[#FF3B30]/15 text-zinc-500 hover:text-[#FF3B30]"><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={stopLive} data-testid="stop-live" className="w-full py-4 rounded-xl bg-[#FF3B30] text-white font-bold hover:bg-[#E22b21] transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#FF3B30]/20">
                    <Square className="w-5 h-5" fill="currentColor" /> Stop Broadcast
                  </button>
                </div>
              )}
              <p className="text-xs text-zinc-500 mt-4">Real-time voice uses WebRTC (STUN + TURN relay). For unlimited reach, publish episodes.</p>
            </div>

            {/* Calls in queue */}
            <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="calls-in-queue-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                  <h3 className="font-display font-bold text-lg flex items-center gap-2">
                    <PhoneCall className="w-5 h-5 text-[#E6B34A]" /> Calls in queue
                    {phoneCallers.filter((c) => !c.on_air).length > 0 && (
                      <span data-testid="queue-count" className="px-2 py-0.5 rounded-full bg-emerald-500 text-[#0A1030] text-xs font-extrabold animate-pulse">{phoneCallers.filter((c) => !c.on_air).length} waiting</span>
                    )}
                  </h3>
                  <p className="text-sm text-zinc-500">Phone callers waiting to come on the air.</p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {voiceCfg?.number_display && (
                    <div className="text-right">
                      <p className="overline text-zinc-500">Studio number</p>
                      <p className="font-display font-extrabold text-xl text-[#E6B34A]" data-testid="queue-studio-number">{voiceCfg.number_display}</p>
                    </div>
                  )}
                  {voiceCfg?.configured && (
                    <button onClick={toggleLineClosed} disabled={lineBusy} data-testid="queue-toggle-line" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-xs transition-colors disabled:opacity-60 ${lineClosed ? "bg-emerald-500 text-[#0A1030] hover:bg-emerald-400" : "bg-red-500 text-white hover:bg-red-600"}`}>
                      {lineBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5" />} {lineClosed ? "Open the line" : "Close the line"}
                    </button>
                  )}
                </div>
              </div>
              {!voiceCfg?.configured ? (
                <div className="rounded-xl border border-[#E6B34A]/30 bg-[#E6B34A]/5 p-4 text-sm text-zinc-300" data-testid="queue-not-configured">
                  <AlertTriangle className="w-4 h-4 text-[#E6B34A] inline mr-1.5" /> The phone call-in line isn't set up yet.
                </div>
              ) : (
                <div>
                  {callInJoined ? (
                    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#0C1338] border border-emerald-500/20 px-3 py-2 mb-3" data-testid="queue-line-connected">
                      <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-bold"><span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" /> Line connected</span>
                      <button onClick={leaveCallIn} data-testid="queue-disconnect-line" className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-colors">Disconnect</button>
                    </div>
                  ) : live ? (
                    <button onClick={joinCallIn} disabled={callInBusy} data-testid="queue-connect-line" className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 mb-3">
                      {callInBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />} Connect call-in line
                    </button>
                  ) : (
                    <p className="text-[11px] text-zinc-500 mb-3 text-center" data-testid="queue-golive-hint">Go live and connect the line to bring callers on the air. Waiting callers still show below.</p>
                  )}
                  {!callInJoined && live && <p className="text-[11px] text-zinc-500 mb-2 text-center">Drag waiting callers to reorder — the top of the list goes on air next.</p>}
                  <label className="flex items-center gap-2 text-[11px] text-zinc-400 mb-3 cursor-pointer" data-testid="auto-connect-row">
                    <input type="checkbox" checked={autoConnectLine} onChange={(e) => setAutoConnectLine(e.target.checked)} data-testid="auto-connect-toggle" className="accent-[#E6B34A] w-3.5 h-3.5" />
                    Auto-connect the call-in line when I go live
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-zinc-400 mb-3 cursor-pointer" data-testid="mute-hold-headset-row">
                    <input type="checkbox" checked={muteHoldInHeadset} onChange={(e) => setMuteHoldInHeadset(e.target.checked)} data-testid="mute-hold-headset-toggle" className="accent-[#E6B34A] w-3.5 h-3.5" />
                    Mute hold music in my headset <span className="text-zinc-600">(listeners still hear it; auto-unmutes when a caller is on air)</span>
                  </label>
                  <div className="flex items-center gap-2 flex-wrap text-[11px] text-zinc-400 mb-3" data-testid="auto-drop-row">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={autoDrop.enabled} onChange={(e) => saveAutoDrop({ enabled: e.target.checked })} data-testid="auto-drop-toggle" className="accent-[#E6B34A] w-3.5 h-3.5" />
                      Long-hold auto-handle after
                    </label>
                    <select value={autoDrop.minutes} onChange={(e) => saveAutoDrop({ minutes: Number(e.target.value) })} data-testid="auto-drop-minutes" disabled={!autoDrop.enabled} className="rounded bg-[#1E2A5A] border border-white/10 px-1.5 py-0.5 text-[11px] disabled:opacity-50">
                      {[5, 10, 15, 20, 30].map((m) => <option key={m} value={m}>{m} min</option>)}
                    </select>
                    <select value={autoDrop.action} onChange={(e) => saveAutoDrop({ action: e.target.value })} data-testid="auto-drop-action" disabled={!autoDrop.enabled} className="rounded bg-[#1E2A5A] border border-white/10 px-1.5 py-0.5 text-[11px] disabled:opacity-50">
                      <option value="sms_drop">text &amp; drop</option>
                      <option value="drop">drop only</option>
                      <option value="sms">text only</option>
                    </select>
                  </div>
                  {callInJoined && phoneCallers.some((c) => c.on_air || c.state === "screening") && (
                    <div className="mb-3 rounded-xl border border-white/5 bg-[#0C1338] px-3 py-2.5" data-testid="caller-volume-trim">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-zinc-400 inline-flex items-center gap-1.5"><Volume2 className="w-3 h-3 text-[#E6B34A]" /> Caller volume <span className="text-zinc-600">vs your mic</span></span>
                        <span className="text-[10px] text-zinc-500 tabular-nums">{phoneLevel}%</span>
                      </div>
                      <input type="range" min={0} max={150} step={5} value={phoneLevel} onChange={(e) => changePhoneLevel(Number(e.target.value))} data-testid="caller-volume" className="w-full accent-[#E6B34A]" />
                    </div>
                  )}
                  {callInJoined && phoneCallers.some((c) => !c.on_air) && (
                    <button onClick={airNextCaller} data-testid="air-next-caller" className="w-full mb-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-[#0A1030] font-bold text-sm hover:bg-emerald-400 transition-colors"><Radio className="w-4 h-4" /> Air next caller</button>
                  )}
                  {callInJoined && phoneCallers.some((c) => !c.on_air) && <p className="text-[10px] text-zinc-500 mb-3 text-center">Tip: press <kbd className="px-1 py-0.5 rounded bg-white/10 text-zinc-300 font-mono">Space</kbd> to air the next caller instantly</p>}
                  <div className="space-y-2" data-testid="queue-list">
                    {phoneCallers.length === 0 && <p className="text-sm text-zinc-500 py-6 text-center">No callers yet. Share the studio number on air so listeners can call in.</p>}
                    {phoneCallers.map((c) => (
                      <div key={c.call_sid} draggable={!c.on_air} onDragStart={() => onDragStartCaller(c.call_sid)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropCaller(c.call_sid)} className={`rounded-xl px-4 py-3 border transition-colors ${onAirFlashSids.includes(c.call_sid) ? "border-emerald-400 ring-2 ring-emerald-400/70 bg-emerald-500/25 animate-pulse" : flashSids.includes(c.call_sid) ? "border-[#E6B34A] ring-2 ring-[#E6B34A]/70 bg-[#E6B34A]/10 animate-pulse" : c.on_air ? "border-emerald-500/40 bg-emerald-500/5" : heldTooLong(c.call_sid) ? "border-amber-500/60 bg-amber-500/10 animate-pulse cursor-grab active:cursor-grabbing" : "border-white/5 bg-[#0C1338] cursor-grab active:cursor-grabbing"}`} data-testid={`queue-caller-${c.call_sid}`}>
                        <div className="flex items-center gap-3">
                          {!c.on_air && <ArrowDownWideNarrow className="w-3.5 h-3.5 text-zinc-600 shrink-0 rotate-90" title="Drag to reorder — top of the list goes on air next" />}
                          <PhoneCall className={`w-4 h-4 shrink-0 ${c.on_air ? "text-emerald-400" : "text-[#E6B34A]"}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                              <span className="truncate">{c.caller_name || c.from_display}</span>
                              {c.caller_city && <span data-testid={`queue-city-${c.call_sid}`} className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#1E2A5A] text-[#E6B34A] text-[10px] font-bold"><MapPin className="w-2.5 h-2.5" />{c.caller_city}</span>}
                              {!c.on_air && phoneCallers.find((x) => !x.on_air)?.call_sid === c.call_sid && <span data-testid={`queue-nextup-${c.call_sid}`} className="shrink-0 px-1.5 py-0.5 rounded-full bg-[#E6B34A] text-[#0A1030] text-[10px] font-bold">NEXT UP</span>}
                            </p>
                            <p className="text-[11px] text-zinc-500 truncate">{c.caller_name ? `${c.from_display} · ` : ""}{c.on_air ? <span className="text-emerald-400 font-semibold tabular-nums">On air {elapsedStr(c.call_sid)}</span> : c.state === "screening" ? <span className="text-sky-400 font-semibold tabular-nums">Off air · talking privately {screenStr(c.call_sid)}</span> : <span className={`tabular-nums ${heldTooLong(c.call_sid) ? "text-amber-400 font-bold" : ""}`}>Holding {waitStr(c.call_sid)}{heldTooLong(c.call_sid) ? " · waiting a while" : ""}</span>}</p>
                          </div>
                          {c.on_air ? (
                            <>
                              <button onClick={() => takeOffAir(c.call_sid)} data-testid={`queue-offair-${c.call_sid}`} title="Take off air but keep talking privately (listeners won't hear them)" className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-colors">Take off air</button>
                              <button onClick={() => holdCaller(c.call_sid)} data-testid={`queue-hold-${c.call_sid}`} title="Send back to hold music" className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-colors">Hold</button>
                            </>
                          ) : c.state === "screening" ? (
                            <>
                              <button onClick={() => putOnAir(c.call_sid)} disabled={!callInJoined} data-testid={`queue-onair-${c.call_sid}`} title="Bring this caller (back) on air" className="px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Back on air</button>
                              <button onClick={() => holdCaller(c.call_sid)} data-testid={`queue-hold-${c.call_sid}`} title="Send to hold music" className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-colors">Hold</button>
                            </>
                          ) : (
                            <button onClick={() => putOnAir(c.call_sid)} disabled={!callInJoined} data-testid={`queue-onair-${c.call_sid}`} title={callInJoined ? "Bring this caller on air" : "Connect the call-in line first"} className="px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Put on air</button>
                          )}
                          <button onClick={() => prefillShoutout(c)} data-testid={`queue-shoutout-${c.call_sid}`} title="Create an on-air shout-out for this caller" className="px-2.5 py-1.5 rounded-lg bg-[#1E2A5A] text-[#E6B34A] text-xs font-bold hover:bg-[#E6B34A] hover:text-[#0A1030] transition-colors inline-flex items-center gap-1 shrink-0"><Wand2 className="w-3.5 h-3.5" /> Shout-out</button>
                          <button onClick={() => dropCaller(c.call_sid)} data-testid={`queue-drop-${c.call_sid}`} title="Drop call" className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><PhoneOff className="w-4 h-4" /></button>
                        </div>
                        <input
                          defaultValue={c.note || ""}
                          key={`note-${c.call_sid}-${c.note || ""}`}
                          onBlur={(e) => { if ((e.target.value || "").trim() !== (c.note || "")) saveNote(c.call_sid, e.target.value.trim()); }}
                          placeholder="Screener note — what they want to say…"
                          data-testid={`queue-note-${c.call_sid}`}
                          className="w-full mt-2 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#E6B34A]"
                        />
                      </div>
                    ))}
                  </div>
                  {missedCallers.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-white/5" data-testid="missed-callers">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-zinc-400 flex items-center gap-1.5"><PhoneMissed className="w-3.5 h-3.5 text-[#FF3B30]" /> Missed calls ({missedCallers.length})</p>
                        <button onClick={clearMissed} data-testid="clear-missed" className="text-[11px] text-zinc-500 hover:text-zinc-300">Clear</button>
                      </div>
                      <div className="space-y-1.5 max-h-52 overflow-y-auto">
                        {missedCallers.map((m) => (
                          <div key={m.id} className="flex items-center gap-3 rounded-lg bg-[#0C1338] px-3 py-2" data-testid={`missed-${m.id}`}>
                            <PhoneMissed className="w-3.5 h-3.5 text-[#FF3B30] shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold truncate">{m.caller_name || m.from_display}{m.caller_city ? ` · ${m.caller_city}` : ""}</p>
                              <p className="text-[10px] text-zinc-500 truncate">{m.from_display}{m.note ? ` · ${m.note}` : ""} · {fmt(m.at)}</p>
                            </div>
                            {m.from && <a href={`tel:${m.from}`} data-testid={`callback-${m.id}`} title="Call back" className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors"><PhoneCall className="w-3.5 h-3.5" /></a>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {live && (
            <div className="mt-6 rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="broadcast-delay-panel">
              <div className="flex items-center gap-3 flex-wrap">
                <ShieldCheck className="w-5 h-5 text-[#E6B34A] shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-lg">Broadcast delay</h3>
                  <p className="text-sm text-zinc-500">Air on a short delay so you can dump anything before listeners hear it. You always monitor in realtime.</p>
                </div>
                <div className="flex gap-1 ml-auto">
                  {[[0, "Off"], [3, "3s"], [6, "6s"], [10, "10s"]].map(([sec, lbl]) => (
                    <button key={sec} onClick={() => changeBcDelay(sec)} data-testid={`bc-delay-${sec}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${bcDelay === sec ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300 hover:bg-white/10"}`}>{lbl}</button>
                  ))}
                </div>
                <button onClick={dumpNow} disabled={bcDelay === 0} data-testid="dump-broadcast" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold bg-[#FF3B30] text-white hover:bg-[#E22b21] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <AlertTriangle className="w-4 h-4" /> DUMP
                </button>
              </div>
              {bcDelay > 0 && <p className="text-[11px] text-[#E6B34A] mt-3" data-testid="bc-delay-note">On air {bcDelay}s behind live — hit DUMP to cut the last {bcDelay}s before it airs.</p>}
              <div className="mt-3 pt-3 border-t border-white/5" data-testid="auto-dump-words">
                <label className="overline text-zinc-400 flex items-center gap-1.5 mb-1.5"><AlertTriangle className="w-3 h-3 text-[#E6B34A]" /> Auto-dump words</label>
                <div className="flex gap-2">
                  <Input value={flaggedWords} onChange={(e) => setFlaggedWords(e.target.value)} placeholder="e.g. word one, word two, word three" data-testid="flagged-words" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm" />
                  <button onClick={saveFlaggedWords} data-testid="save-flagged" className="shrink-0 px-4 rounded-lg bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors">Save</button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1.5">If a caller says any of these on air, the broadcast auto-dumps. Requires a delay above and Twilio transcription.</p>
                {dumpLog.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5" data-testid="dump-log">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="overline text-zinc-400">Recent dumps</span>
                      <button onClick={exportDumpLog} data-testid="export-dump-log" className="text-[11px] font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors">Export CSV</button>
                    </div>
                    <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto">
                      {dumpLog.slice(0, 8).map((e, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]" data-testid={`dump-entry-${i}`}>
                          <span className="text-zinc-300">{e.kind === "auto" ? <span className="text-[#FF3B30] font-semibold">Auto</span> : "Manual"}{e.word ? ` · "${e.word}"` : ""}</span>
                          <span className="text-zinc-500">{fmtDateTime(e.at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {live && (
            <div className="mt-6 rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="bg-music-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><Music className="w-5 h-5 text-[#E6B34A]" /> Background music &amp; sermons</h3>
                {bgPlaying && <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-bold"><span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" /> Playing</span>}
              </div>
              <p className="text-sm text-zinc-500 mb-4">Play a station or an uploaded track under your show. {bgOnAir ? "Listeners hear it beneath your voice." : "Studio-only right now — raise your headphone level to hear it."}</p>

              <div className={`mb-4 rounded-xl bg-[#0C1338] border p-3 transition-colors ${bgPlaying ? "border-emerald-500/30" : "border-white/10"}`} data-testid="bg-controls">
                <div className="flex items-center gap-3 flex-wrap">
                  <Music className={`w-4 h-4 shrink-0 ${bgPlaying ? "text-emerald-400" : "text-zinc-500"}`} />
                  <span className={`text-sm font-semibold flex-1 min-w-0 truncate ${bgPlaying ? "" : "text-zinc-500"}`} data-testid="bg-playing-label">{bgPlaying ? bgPlaying.label : "Nothing playing yet — pick a station or track below"}</span>
                  {bgPlaying && <button onClick={stopBg} data-testid="bg-stop" className="px-3 py-1.5 rounded-lg bg-[#FF3B30]/90 text-white text-xs font-bold hover:bg-[#FF3B30] transition-colors">Stop</button>}
                </div>
                <div className="flex items-center gap-2 mt-2.5">
                  <div className="h-1.5 flex-1 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-emerald-400 transition-[width] duration-75" style={{ width: `${Math.round(bgMeter * 100)}%` }} /></div>
                  <Volume2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <input type="range" min={0} max={100} step={5} value={bgLevel} onChange={(e) => changeBgLevel(Number(e.target.value))} data-testid="bg-level" className="w-28 accent-[#E6B34A]" />
                  <span className="text-[10px] text-zinc-500 tabular-nums w-8 text-right">{bgLevel}%</span>
                </div>
                <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                  <button onClick={toggleBgOnAir} data-testid="bg-onair-toggle" className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${bgOnAir ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>{bgOnAir ? "On air" : "Studio only"}</button>
                  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer"><input type="checkbox" checked={bgDuck} onChange={toggleBgDuck} data-testid="bg-duck" className="accent-[#E6B34A] w-4 h-4" /> Lower music when I talk</label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-white/5" data-testid="bg-duck-depths">
                  <div>
                    <div className="flex items-center justify-between mb-1"><span className="text-[11px] text-zinc-400">Dip when I talk</span><span className="text-[10px] text-zinc-500 tabular-nums">{duckTalk}%</span></div>
                    <input type="range" min={0} max={95} step={5} value={duckTalk} onChange={(e) => { const v = Number(e.target.value); setDuckTalk(v); applyDuckDepths(v, duckCaller); }} data-testid="duck-talk" className="w-full accent-[#E6B34A]" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1"><span className="text-[11px] text-zinc-400">Dip for callers</span><span className="text-[10px] text-zinc-500 tabular-nums">{duckCaller}%</span></div>
                    <input type="range" min={0} max={95} step={5} value={duckCaller} onChange={(e) => { const v = Number(e.target.value); setDuckCaller(v); applyDuckDepths(duckTalk, v); }} data-testid="duck-caller" className="w-full accent-[#E6B34A]" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/5" data-testid="caller-bed-level">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-zinc-400 inline-flex items-center gap-1.5"><PhoneCall className="w-3 h-3 text-[#E6B34A]" /> Music level for phone callers</span>
                    <span className="text-[10px] text-zinc-500 tabular-nums">{callerMusicLevel}%</span>
                  </div>
                  <input type="range" min={0} max={150} step={5} value={callerMusicLevel} onChange={(e) => changeCallerMusicLevel(Number(e.target.value))} data-testid="caller-music-level" className="w-full accent-[#E6B34A]" />
                  <p className="text-[10px] text-zinc-500 mt-1">How loud the music bed sits for callers vs listeners (100% = same as listeners).</p>
                </div>
                {bgPlaying && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5 flex-wrap" data-testid="bg-sleep">
                    <span className="text-[11px] text-zinc-400 inline-flex items-center gap-1.5">Sleep timer{bgSleepAt && <span className="text-[#E6B34A] font-mono font-bold tabular-nums">· {bgSleepLeft}</span>}</span>
                    {[15, 30, 45, 60].map((m) => (
                      <button key={m} onClick={() => setBgSleep(m)} data-testid={`bg-sleep-${m}`} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors ${bgSleepMin === m ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300 hover:bg-white/10"}`}>{m}m</button>
                    ))}
                    {bgSleepAt && <button onClick={() => setBgSleep(0)} data-testid="bg-sleep-off" className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-white/5 text-[#FF7A70] hover:bg-[#FF3B30]/10 transition-colors">Off</button>}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="overline text-zinc-400">Station presets</label>
                  <button onClick={() => setBgManage((v) => !v)} data-testid="bg-manage-presets" className="text-[11px] font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors">{bgManage ? "Done" : "Manage"}</button>
                </div>
                {!bgManage ? (
                  <div className="flex flex-wrap gap-2" data-testid="bg-preset-row">
                    {bgPresetIds.length === 0 && <p className="text-sm text-zinc-500">No presets yet — tap Manage to add stations.</p>}
                    {bgStations.filter((s) => bgPresetIds.includes(s.id)).map((s) => (
                      <button key={s.id} onClick={() => playBg(s.stream_url, s.name, false)} disabled={s.broadcastable === false && bgOnAir} data-testid={`bg-play-station-${s.id}`} className="px-3 py-1.5 rounded-full bg-[#1E2A5A] text-zinc-200 text-xs font-bold hover:bg-white/10 transition-colors disabled:opacity-40" title={s.broadcastable === false ? "Studio-only (can't broadcast this stream)" : ""}>
                        ▶ {s.name}{s.broadcastable === false ? " · studio only" : ""}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-56 overflow-auto pr-1" data-testid="bg-station-manage">
                    {bgStations.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer rounded-lg bg-[#0C1338] border border-white/5 px-3 py-2">
                        <input type="checkbox" checked={bgPresetIds.includes(s.id)} onChange={() => togglePreset(s.id)} data-testid={`bg-preset-toggle-${s.id}`} className="accent-[#E6B34A] w-4 h-4" />
                        <span className="flex-1 min-w-0 truncate">{s.name}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.broadcastable === false ? "bg-amber-500/20 text-amber-300" : s.broadcastable ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-zinc-400"}`}>{s.broadcastable === false ? "studio only" : s.broadcastable ? "on-air ok" : "checking…"}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-[#0C1338] border border-white/5 p-3" data-testid="bg-library">
                <label className="overline text-zinc-400 mb-2 block">Your music &amp; sermons</label>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <Input value={bgUploadTitle} onChange={(e) => setBgUploadTitle(e.target.value)} placeholder="Title (optional)" data-testid="bg-upload-title" className="flex-1 min-w-[140px] bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm h-9" />
                  <select value={bgUploadKind} onChange={(e) => setBgUploadKind(e.target.value)} data-testid="bg-upload-kind" className="rounded-lg bg-[#1E2A5A] border border-white/10 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]"><option value="music">Music</option><option value="sermon">Sermon</option></select>
                  <label data-testid="bg-upload" className="px-3 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors cursor-pointer inline-flex items-center gap-1.5">
                    {bgUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
                    <input type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg" className="hidden" disabled={bgUploading} onChange={(e) => { uploadBgLibrary(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>
                <div className="space-y-1.5 max-h-56 overflow-auto pr-1">
                  {bgLibrary.length === 0 && <p className="text-sm text-zinc-500">No uploads yet. Add worship beds or a sermon to play under your show.</p>}
                  {bgLibrary.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 rounded-lg bg-[#141C42] border border-white/5 px-3 py-2" data-testid={`bg-lib-${m.id}`}>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${m.kind === "sermon" ? "bg-blue-500/20 text-blue-300" : "bg-[#E6B34A]/20 text-[#E6B34A]"}`}>{m.kind === "sermon" ? "Sermon" : "Music"}</span>
                      <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{m.title}</p>{m.artist && <p className="text-[11px] text-zinc-500 truncate">{m.artist}</p>}</div>
                      <button onClick={() => playBg(`${api.defaults.baseURL}${m.audio_url}`, m.title, m.kind === "music", m.kind)} data-testid={`bg-play-lib-${m.id}`} className="px-3 py-1.5 rounded-lg bg-[#1E2A5A] text-zinc-200 text-xs font-bold hover:bg-white/10 transition-colors">▶ Play</button>
                      <button onClick={() => deleteBgLibrary(m.id)} data-testid={`bg-del-lib-${m.id}`} title="Delete" className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-3 rounded-xl bg-[#0C1338] border border-white/5 p-3" data-testid="bg-playlists">
                <div className="flex items-center justify-between mb-2">
                  <label className="overline text-zinc-400">Sermon playlists</label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setPlShuffle((v) => !v)} data-testid="bg-shuffle" title="Shuffle playback order" className={`text-[11px] font-bold transition-colors ${plShuffle ? "text-[#E6B34A]" : "text-zinc-500 hover:text-zinc-300"}`}>⇄ Shuffle {plShuffle ? "on" : "off"}</button>
                    <button onClick={() => (plBuild ? (setPlBuild(false), setPlEditId(null)) : openNewPlaylist())} data-testid="bg-new-playlist" className="text-[11px] font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors">{plBuild ? "Cancel" : "New playlist"}</button>
                  </div>
                </div>
                {plBuild ? (
                  <div className="space-y-2" data-testid="bg-playlist-builder">
                    <Input value={plName} onChange={(e) => setPlName(e.target.value)} placeholder="Playlist name (e.g. Sunday Series)" data-testid="pl-name" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm h-9" />
                    <p className="text-[11px] text-zinc-500">Check tracks to include:</p>
                    <div className="space-y-1.5 max-h-40 overflow-auto pr-1">
                      {bgLibrary.length === 0 && <p className="text-sm text-zinc-500">Upload some tracks first, then group them here.</p>}
                      {bgLibrary.map((m) => (
                        <label key={m.id} className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer rounded-lg bg-[#141C42] border border-white/5 px-3 py-2">
                          <input type="checkbox" checked={plSelected.includes(m.id)} onChange={() => togglePlItem(m.id)} data-testid={`pl-pick-${m.id}`} className="accent-[#E6B34A] w-4 h-4" />
                          <span className="flex-1 min-w-0 truncate">{m.title}</span>
                          <span className="text-[10px] text-zinc-500">{m.kind}</span>
                        </label>
                      ))}
                    </div>
                    {plSelected.length > 0 && (
                      <div data-testid="pl-order">
                        <p className="text-[11px] text-zinc-500 mb-1">Drag to set play order:</p>
                        <div className="space-y-1.5">
                          {plSelected.map((id, i) => {
                            const m = bgLibrary.find((x) => x.id === id); if (!m) return null;
                            return (
                              <div key={id} draggable onDragStart={() => { plDragIdx.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => { reorderPl(plDragIdx.current, i); plDragIdx.current = null; }} data-testid={`pl-order-${id}`} className="flex items-center gap-2 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 cursor-grab active:cursor-grabbing">
                                <ArrowDownWideNarrow className="w-3.5 h-3.5 text-zinc-500 shrink-0 rotate-90" />
                                <span className="text-[11px] text-[#E6B34A] font-bold tabular-nums w-4 shrink-0">{i + 1}</span>
                                <span className="text-sm flex-1 min-w-0 truncate">{m.title}</span>
                                <button onClick={() => togglePlItem(id)} className="p-1 rounded hover:bg-white/10 text-zinc-400 shrink-0"><X className="w-3.5 h-3.5" /></button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <button onClick={savePlaylist} data-testid="pl-save" className="w-full py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors">{plEditId ? "Update" : "Save"} playlist ({plSelected.length})</button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {bgPlaylists.length === 0 && <p className="text-sm text-zinc-500">No playlists yet — tap "New playlist" to group sermons that play back-to-back.</p>}
                    {bgPlaylists.map((pl) => (
                      <div key={pl.id} className="flex items-center gap-2 rounded-lg bg-[#141C42] border border-white/5 px-3 py-2" data-testid={`bg-playlist-${pl.id}`}>
                        <ScrollText className="w-4 h-4 text-[#E6B34A] shrink-0" />
                        <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{pl.name}</p><p className="text-[11px] text-zinc-500">{pl.items?.length || 0} track{(pl.items?.length || 0) === 1 ? "" : "s"}</p></div>
                        <button onClick={() => playPlaylist(pl)} data-testid={`pl-play-${pl.id}`} className="px-3 py-1.5 rounded-lg bg-[#1E2A5A] text-zinc-200 text-xs font-bold hover:bg-white/10 transition-colors">▶ Play</button>
                        <button onClick={() => editPlaylist(pl)} data-testid={`pl-edit-${pl.id}`} title="Edit" className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deletePlaylist(pl.id)} data-testid={`pl-del-${pl.id}`} title="Delete" className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-3 rounded-xl bg-[#0C1338] border border-white/5 p-3" data-testid="bg-jingles">
                <div className="flex items-center justify-between mb-2">
                  <label className="overline text-zinc-400 flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5 text-[#E6B34A]" /> Jingles &amp; station IDs</label>
                  <a href="/admin?tab=jingles" className="text-[11px] font-bold text-[#E6B34A] hover:text-[#CF9A34] transition-colors">Create</a>
                </div>
                {liveJingles.length === 0 ? (
                  <p className="text-sm text-zinc-500">No jingles yet — create AI station IDs in Admin → Jingles, then fire them on air here.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="jingle-fire-row">
                    {liveJingles.map((j) => (
                      <div key={j.id} className={`inline-flex items-center rounded-full overflow-hidden border ${j.favorite ? "border-[#E6B34A]" : "border-white/10"}`}>
                        <button onClick={() => fireJingle(j)} data-testid={`fire-jingle-${j.id}`} className="px-3 py-1.5 bg-[#1E2A5A] text-zinc-200 text-xs font-bold hover:bg-[#E6B34A] hover:text-[#0A1030] transition-colors inline-flex items-center gap-1.5"><Radio className="w-3.5 h-3.5" /> {j.title} <span className="text-[10px] opacity-70">{j.length_sec}s</span></button>
                        <button onClick={() => toggleFavJingle(j)} data-testid={`fav-jingle-${j.id}`} title={j.favorite ? "Unpin" : "Pin to top"} className={`px-2 py-1.5 bg-[#1E2A5A] border-l border-white/10 transition-colors ${j.favorite ? "text-[#E6B34A]" : "text-zinc-500 hover:text-[#E6B34A]"}`}><Star className="w-3.5 h-3.5" fill={j.favorite ? "currentColor" : "none"} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-white/5" data-testid="shoutout-form" ref={shoutFormRef}>
                  {cotd && (
                    <div className="mb-3 rounded-xl border border-[#E6B34A] bg-gradient-to-r from-[#E6B34A]/15 to-transparent p-3" data-testid="cotd-card">
                      <div className="flex items-center gap-3">
                        <Crown className="w-5 h-5 text-[#E6B34A] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold text-[#E6B34A] uppercase tracking-wide">Caller of the Day</p>
                          <p className="text-sm font-bold text-white truncate">{cotd.title.replace("Shout-out: ", "")}</p>
                        </div>
                        <button onClick={() => replayShoutout(cotd)} data-testid="cotd-play" className="px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors inline-flex items-center gap-1.5 shrink-0"><Volume2 className="w-3.5 h-3.5" /> Play on air</button>
                        <button onClick={() => toggleCotd(cotd)} data-testid="cotd-clear" title="Clear Caller of the Day" className="p-1.5 rounded-lg text-zinc-400 hover:text-[#FF3B30] shrink-0"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}
                  <label className="overline text-zinc-400 flex items-center gap-1.5 mb-2"><UserPlus className="w-3.5 h-3.5 text-[#E6B34A]" /> Instant listener shout-out</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input value={shoutName} onChange={(e) => setShoutName(e.target.value)} placeholder="Listener's name" data-testid="shoutout-name" className="flex-1 min-w-[130px] bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm h-9" />
                    <Input value={shoutMsg} onChange={(e) => setShoutMsg(e.target.value)} placeholder="Optional note (e.g. from Dallas)" data-testid="shoutout-message" className="flex-[2] min-w-[160px] bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm h-9" />
                    <button onClick={fireShoutout} disabled={shoutBusy} data-testid="shoutout-fire" className="px-3 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 inline-flex items-center gap-1.5">{shoutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Shout it out</button>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1.5">AI writes &amp; voices a personalized shout-out, then plays it on air instantly.</p>
                  <label className="flex items-center gap-2 mt-2 text-[11px] text-zinc-400 cursor-pointer w-fit">
                    <input type="checkbox" checked={cotdAuto} onChange={toggleCotdAuto} className="accent-[#E6B34A] w-3.5 h-3.5" data-testid="cotd-auto-reset" />
                    <Crown className="w-3 h-3 text-[#E6B34A]" /> Clear Caller of the Day automatically each morning
                  </label>
                  {shoutHistory.length > 0 && (
                    <div className="mt-3 space-y-1.5" data-testid="shoutout-history">
                      <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-wide">Recent shout-outs — tap to replay</label>
                      {shoutHistory.map((s) => (
                        <div key={s.id} className={`flex items-center gap-2 rounded-lg bg-[#1E2A5A] px-2.5 py-1.5 ${s.cotd ? "border border-[#E6B34A]" : "border border-white/5"}`} data-testid={`shoutout-item-${s.id}`}>
                          <button onClick={() => replayShoutout(s)} data-testid={`shoutout-replay-${s.id}`} title="Replay on air" className="w-7 h-7 rounded-full bg-[#E6B34A] text-[#0A1030] flex items-center justify-center shrink-0"><Volume2 className="w-3.5 h-3.5" /></button>
                          <span className="text-xs text-zinc-200 flex-1 min-w-0 truncate flex items-center gap-1">{s.cotd && <Crown className="w-3 h-3 text-[#E6B34A] shrink-0" fill="currentColor" />}{s.title.replace("Shout-out: ", "")}</span>
                          <button onClick={() => toggleCotd(s)} data-testid={`shoutout-cotd-${s.id}`} title="Caller of the Day" className={`p-1 rounded shrink-0 ${s.cotd ? "text-[#E6B34A]" : "text-zinc-500 hover:text-[#E6B34A]"}`}><Crown className="w-3.5 h-3.5" fill={s.cotd ? "currentColor" : "none"} /></button>
                          <button onClick={() => favShoutout(s)} data-testid={`shoutout-fav-${s.id}`} title="Favorite" className={`p-1 rounded shrink-0 ${s.favorite ? "text-[#E6B34A]" : "text-zinc-500 hover:text-zinc-300"}`}><Star className="w-3.5 h-3.5" fill={s.favorite ? "currentColor" : "none"} /></button>
                          <button onClick={() => delShoutout(s.id)} data-testid={`shoutout-del-${s.id}`} title="Delete" className="p-1 rounded text-zinc-500 hover:text-[#FF3B30] shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}


          {voiceCfg && (
            <div className="mt-6 rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="phone-callin-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><PhoneCall className="w-5 h-5 text-[#E6B34A]" /> Phone call-in line</h3>
                  <p className="text-sm text-zinc-500">Listeners dial in by phone and wait on hold. Tap "Put on air" to mix a caller into the live broadcast.</p>
                </div>
                {voiceCfg?.number_display && (
                  <div className="text-right shrink-0">
                    <p className="overline text-zinc-500">Studio number</p>
                    <p className="font-display font-extrabold text-xl text-[#E6B34A]" data-testid="callin-number">{voiceCfg.number_display}</p>
                  </div>
                )}
              </div>

              {voiceCfg?.configured && (
                <div className={`mt-4 rounded-xl border p-3 ${lineClosed ? "border-red-500/40 bg-red-500/5" : "border-white/5 bg-[#0C1338]"}`} data-testid="line-status-editor">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <label className="overline text-zinc-400 flex items-center gap-1.5 mb-0.5"><PhoneCall className="w-3 h-3 text-[#E6B34A]" /> Call-in line {lineClosed ? <span className="text-red-400 font-bold">· CLOSED</span> : <span className="text-emerald-400 font-bold">· OPEN</span>}</label>
                      <p className="text-[11px] text-zinc-500">{lineClosed ? "Callers hear your closed message, then the call ends." : "Callers are greeted and queued to come on the air."}</p>
                    </div>
                  </div>
                  <div className="mt-2.5">
                    <label className="text-[11px] text-zinc-500 mb-1 block">Message callers hear when the line is closed</label>
                    <Textarea value={lineMsg} onChange={(e) => setLineMsg(e.target.value)} placeholder="Thanks for calling In His Image Radio! Our call-in line is closed right now…" data-testid="line-closed-message" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm min-h-[60px]" />
                    <div className="flex justify-end mt-2">
                      <button onClick={async () => { try { const { data } = await api.post("/voice/line-status", { closed: lineClosed, message: lineMsg }); setLineMsg(data.message || ""); toast.success("Closed message saved"); } catch (e) { toast.error("Could not save message"); } }} data-testid="save-line-message" className="px-4 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors">Save message</button>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-white/5" data-testid="auto-schedule">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <label className="flex items-center gap-2 text-[13px] text-zinc-200 cursor-pointer" data-testid="auto-schedule-toggle-row">
                        <input type="checkbox" checked={autoEnabled} disabled={schedBusy} onChange={toggleAuto} data-testid="auto-schedule-toggle" className="accent-[#E6B34A] w-4 h-4" />
                        <CalendarClock className="w-4 h-4 text-[#E6B34A]" /> Auto open &amp; close the line on a schedule
                      </label>
                      {autoEnabled && <span className={`text-[11px] font-bold ${voiceCfg?.closed ? "text-red-400" : "text-emerald-400"}`}>{lineClosed ? "Currently closed" : "Currently open"}</span>}
                    </div>
                    {autoEnabled && (
                      <div className="mt-2.5 pl-6 space-y-2">
                        <p className="text-[11px] text-zinc-500">The line opens automatically during any show marked <span className="text-zinc-300 font-semibold">"This show takes calls"</span> (on the Schedule) — plus any custom windows you add below. It closes at all other times. Your manual Close/Open button overrides the schedule until the next window.</p>
                        {windows.map((w, i) => (
                          <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`sched-window-${i}`}>
                            <select value={w.day || "Every day"} onChange={(e) => updateWindow(i, { day: e.target.value })} onBlur={() => saveSchedule(autoEnabled, windows)} data-testid={`sched-day-${i}`} className="rounded-lg bg-[#1E2A5A] border border-white/10 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                              {["Every day", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                            <input type="time" value={w.start || ""} onChange={(e) => updateWindow(i, { start: e.target.value })} onBlur={() => saveSchedule(autoEnabled, windows)} data-testid={`sched-start-${i}`} className="rounded-lg bg-[#1E2A5A] border border-white/10 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
                            <span className="text-xs text-zinc-500">to</span>
                            <input type="time" value={w.end || ""} onChange={(e) => updateWindow(i, { end: e.target.value })} onBlur={() => saveSchedule(autoEnabled, windows)} data-testid={`sched-end-${i}`} className="rounded-lg bg-[#1E2A5A] border border-white/10 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
                            <button onClick={() => removeWindow(i)} data-testid={`sched-remove-${i}`} title="Remove window" className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                        <button onClick={addWindow} disabled={schedBusy} data-testid="sched-add-window" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E2A5A] text-zinc-200 text-xs font-bold hover:bg-white/10 transition-colors"><Plus className="w-3.5 h-3.5" /> Add a call-in window</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {voiceCfg?.configured && (
                <div className="mt-3 rounded-xl border border-white/5 bg-[#0C1338] p-3" data-testid="hold-editor">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="overline text-zinc-400 flex items-center gap-1.5"><Music className="w-3 h-3 text-[#E6B34A]" /> On-hold music &amp; message</label>
                    <button onClick={previewCallerHold} data-testid="preview-caller-hold" title="Hear exactly what a caller on hold hears right now" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${callerPreviewOn ? "bg-[#FF3B30]/20 text-[#FF7A70] hover:bg-[#FF3B30]/30" : "bg-[#E6B34A]/15 text-[#E6B34A] hover:bg-[#E6B34A]/25"}`}>
                      {callerPreviewOn ? <><Square className="w-3 h-3" /> Stop</> : <><Headphones className="w-3 h-3" /> Hear what callers hear now</>}
                    </button>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className="text-[11px] text-zinc-500 mb-1 block">Hold music</label>
                      <select value={holdActivePath ? "custom" : holdMode} onChange={(e) => changeHoldMode(e.target.value)} data-testid="hold-mode" className="w-full rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                        <option value="pad">Soft ambient pad (default)</option>
                        <option value="silence">Silence — no music</option>
                        {holdLibrary.length > 1 && <option value="rotate">Rotate through library</option>}
                        {holdActivePath && <option value="custom">Custom music (from library)</option>}
                      </select>
                      <p className="text-[11px] text-zinc-500 mt-1">{holdActivePath ? "Callers hear your uploaded music. Choose the pad or silence below to stop it." : holdMode === "silence" ? "Callers wait in silence — no hold music plays." : holdMode === "rotate" ? "Callers hear your library on rotation." : "Callers hear a gentle ambient pad. Upload your own tracks below for custom music."}</p>
                      {holdMode === "rotate" && !holdActivePath && (
                        <label className="flex items-center gap-2 text-[11px] text-zinc-400 mt-1.5 cursor-pointer" data-testid="hold-shuffle-row">
                          <input type="checkbox" checked={holdShuffle} onChange={(e) => changeShuffle(e.target.checked)} data-testid="hold-shuffle-toggle" className="accent-[#E6B34A] w-3.5 h-3.5" />
                          Shuffle — play tracks in random order
                        </label>
                      )}
                    </div>
                    <div className="rounded-lg border border-white/10 bg-[#1E2A5A]/40 p-3" data-testid="hold-upload">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-[13px] font-bold text-zinc-100 flex items-center gap-1.5"><Upload className="w-3.5 h-3.5 text-[#E6B34A]" /> Upload custom hold music</p>
                          <p className="text-[11px] text-zinc-500 mt-0.5">MP3 or WAV — add tracks to your library, then switch between them below.</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <label data-testid="upload-hold-audio-main" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors ${holdUploading ? "opacity-60 pointer-events-none bg-[#1E2A5A] text-zinc-300" : "bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]"}`}>
                            {holdUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {holdUploading ? "Uploading…" : "Add track"}
                            <input type="file" accept="audio/mpeg,audio/mp3,audio/wav,.mp3,.wav" className="hidden" onChange={(e) => uploadHoldAudio(e.target.files?.[0])} />
                          </label>
                        </div>
                      </div>
                      {holdUploading && (
                        <div className="mt-3" data-testid="hold-upload-progress">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] text-zinc-400">Uploading your track…</span>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-bold text-[#E6B34A] tabular-nums" data-testid="hold-upload-pct">{holdUploadPct}%</span>
                              {holdUploadPct < 100 && <button onClick={cancelHoldUpload} data-testid="cancel-hold-upload" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FF3B30]/15 text-[#FF7A70] hover:bg-[#FF3B30]/25 transition-colors"><X className="w-3 h-3" /> Cancel</button>}
                            </div>
                          </div>
                          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full bg-[#E6B34A] transition-[width] duration-150" style={{ width: `${holdUploadPct}%` }} />
                          </div>
                          {holdUploadPct >= 100 && <p className="text-[11px] text-zinc-500 mt-1">Finishing up — saving to your library…</p>}
                        </div>
                      )}
                    </div>
                    {holdLibrary.length > 0 && (
                      <div className="rounded-lg border border-white/10 bg-[#0C1338] p-3" data-testid="hold-library">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[11px] font-bold text-zinc-400">Your hold-music library — pick one to play to callers</p>
                          {holdLibrary.some((t) => (t.plays || 0) > 0) && <button onClick={resetPlays} data-testid="reset-plays" className="text-[11px] text-zinc-500 hover:text-zinc-300">Reset counts</button>}
                        </div>
                        <div className="space-y-1.5 max-h-52 overflow-y-auto">
                          {holdLibrary.map((t) => (
                            <div key={t.id} className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${t.id === holdNowPlaying ? "border-[#E6B34A]/60 bg-[#E6B34A]/10" : t.active ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/5 bg-[#1E2A5A]/40"}`} data-testid={`hold-track-${t.id}`}>
                              <Music className={`w-3.5 h-3.5 shrink-0 ${t.id === holdNowPlaying ? "text-[#E6B34A] animate-pulse" : t.active ? "text-emerald-400" : "text-[#E6B34A]"}`} />
                              <span className="text-xs font-semibold truncate flex-1">{t.name}{t.active && <span className="ml-2 text-[10px] text-emerald-400 font-bold">PLAYING</span>}{t.id === holdNowPlaying && <span className="ml-2 text-[10px] text-[#E6B34A] font-bold" data-testid={`now-playing-${t.id}`}>♪ ON HOLD NOW</span>}</span>
                              <span data-testid={`track-plays-${t.id}`} title="Times played on hold" className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 text-[10px] font-bold tabular-nums"><Radio className="w-2.5 h-2.5" />{t.plays || 0}</span>
                              <button onClick={() => previewTrack(t.id)} data-testid={`preview-track-${t.id}`} className="px-2 py-1 rounded bg-white/5 text-zinc-300 text-[11px] font-bold hover:bg-white/10 transition-colors">{holdPreviewing ? "Stop" : "Preview"}</button>
                              {!t.active && <button onClick={() => activateTrack(t.id)} data-testid={`use-track-${t.id}`} className="px-2 py-1 rounded bg-[#E6B34A] text-[#0A1030] text-[11px] font-bold hover:bg-[#CF9A34] transition-colors">Use</button>}
                              <button onClick={() => deleteTrack(t.id)} data-testid={`delete-track-${t.id}`} title="Delete track" className="p-1 rounded hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="text-[11px] text-zinc-500 mb-1 block">Spoken reassurance (optional)</label>
                      <Textarea value={holdText} onChange={(e) => setHoldText(e.target.value)} placeholder="Leave blank for the default 'You're connected — please hold' reassurance, or type your own." data-testid="hold-text" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm min-h-[60px]" />
                    </div>
                    <div className="rounded-lg border border-[#9D4EDD]/30 bg-[#9D4EDD]/5 p-3" data-testid="hold-clip-generator">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-[13px] font-bold text-[#C08BEE] flex items-center gap-1.5"><Wand2 className="w-4 h-4" /> Contemporary gospel hold clip</p>
                          <p className="text-[11px] text-zinc-500 mt-0.5">Builds a 1:30 loop — your reassurance above (or the default) spoken over a warm gospel piano &amp; pad bed. Callers hear it while they wait.</p>
                        </div>
                        <button onClick={generateHoldClip} disabled={holdClipBusy} data-testid="generate-hold-clip" className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9D4EDD]/25 text-[#C08BEE] text-xs font-bold hover:bg-[#9D4EDD]/40 transition-colors disabled:opacity-60">
                          {holdClipBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {holdHasAudio ? "Regenerate clip" : "Generate hold clip"}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <button onClick={saveHold} data-testid="save-hold" className="px-4 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors">Save message</button>
                    </div>
                  </div>
                </div>
              )}

              {!voiceCfg?.configured ? (
                <div className="mt-4 rounded-xl border border-[#E6B34A]/30 bg-[#E6B34A]/5 p-4 text-sm text-zinc-300" data-testid="voice-not-configured">
                  <AlertTriangle className="w-4 h-4 text-[#E6B34A] inline mr-1.5" /> The phone call-in line isn't set up yet. Add your Twilio Voice API Key, Secret and TwiML App SID to switch it on.
                </div>
              ) : !live ? (
                <div className="mt-4 rounded-xl border border-white/5 bg-[#0C1338] p-4 text-sm text-zinc-400" data-testid="voice-need-live">Go live first, then connect the call-in line to bring phone callers on air.</div>
              ) : (
                <div className="mt-4">
                  {!callInJoined ? (
                    <button onClick={joinCallIn} disabled={callInBusy} data-testid="join-callin" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
                      {callInBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />} Connect call-in line
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-bold"><span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" /> Call-in line connected</span>
                      <button onClick={leaveCallIn} data-testid="leave-callin" className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-colors">Disconnect</button>
                    </div>
                  )}

                  <div className="mt-3 rounded-xl bg-[#0C1338] border border-white/5 p-3" data-testid="chime-settings">
                    <label className="flex items-center gap-2 text-[13px] text-zinc-300 cursor-pointer" data-testid="chime-toggle-row">
                      <input type="checkbox" checked={chimeOn} onChange={(e) => setChimeOn(e.target.checked)} data-testid="chime-toggle" className="accent-[#E6B34A] w-4 h-4" />
                      <Radio className="w-4 h-4 text-[#E6B34A]" /> Chime when a new caller joins the queue
                    </label>
                    {chimeOn && (
                      <div className="flex items-center gap-2 mt-2.5 flex-wrap pl-6">
                        <select value={chimeSound} onChange={(e) => setChimeSound(e.target.value)} data-testid="chime-sound" className="rounded-lg bg-[#1E2A5A] border border-white/10 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#E6B34A] capitalize">
                          {Object.keys(CHIME_SOUNDS).map((k) => <option key={k} value={k} className="capitalize">{k}</option>)}
                        </select>
                        <Volume2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                        <input type="range" min={0} max={100} step={5} value={chimeVol} onChange={(e) => setChimeVol(Number(e.target.value))} data-testid="chime-volume" className="w-24 accent-[#E6B34A]" />
                        <span className="text-[10px] text-zinc-500 tabular-nums w-7">{chimeVol}%</span>
                        <button onClick={playChime} data-testid="chime-test" className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#1E2A5A] text-zinc-300 hover:bg-white/10 transition-colors">Test</button>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 rounded-xl bg-[#0C1338] border border-white/5 p-3" data-testid="greeting-editor">
                    <label className="overline text-zinc-400 flex items-center gap-1.5 mb-1.5"><PhoneCall className="w-3 h-3 text-[#E6B34A]" /> Custom call-in greeting</label>
                    <Textarea value={greeting} onChange={(e) => setGreeting(e.target.value)} placeholder="Leave blank to use the default warm welcome. Or type your own greeting — callers hear it, then are asked for their name & city." data-testid="greeting-text" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] text-sm min-h-[70px]" />
                    <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                      <span className="text-[11px] text-zinc-500">{greetingHasAudio ? "An uploaded audio clip is playing to callers (overrides text)." : "Spoken in a natural voice when someone calls."}</span>
                      <div className="flex items-center gap-2">
                        {greetingHasAudio ? (
                          <button onClick={removeGreetingAudio} data-testid="remove-greeting-audio" className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-300 text-xs font-bold hover:bg-white/10 transition-colors">Remove clip</button>
                        ) : (
                          <label data-testid="upload-greeting-audio" className="px-3 py-1.5 rounded-lg bg-[#1E2A5A] text-zinc-200 text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer">
                            Upload clip
                            <input type="file" accept="audio/mpeg,audio/mp3,audio/wav,.mp3,.wav" className="hidden" onChange={(e) => uploadGreetingAudio(e.target.files?.[0])} />
                          </label>
                        )}
                        <button onClick={previewGreeting} data-testid="preview-greeting" className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${greetPreviewing ? "bg-red-500/90 text-white hover:bg-red-600" : "bg-[#1E2A5A] text-zinc-200 hover:bg-white/10"}`}>{greetPreviewing ? "Stop" : "Play a preview"}</button>
                        <button onClick={saveGreeting} data-testid="save-greeting" className="px-4 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors">Save greeting</button>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>
          )}

          {live && (
            <div className="mt-6 rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="callin-panel">
              <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><PhoneCall className="w-5 h-5 text-[#E6B34A]" /> Call-ins</h3>
              <p className="text-sm text-zinc-500 mb-5">Bring listeners on air. Callers' voices are mixed into the live broadcast so everyone hears them.</p>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Raised hands */}
                <div>
                  <p className="overline text-[#E6B34A] mb-3 flex items-center gap-1.5"><Hand className="w-3.5 h-3.5" /> Raised hands ({pendingCalls.length})</p>
                  <div className="space-y-2" data-testid="callin-queue">
                    {pendingCalls.length === 0 ? <p className="text-xs text-zinc-600">No one's waiting to speak.</p> :
                      pendingCalls.map((c) => (
                        <div key={c.id} className="flex items-center gap-2 rounded-xl bg-[#0C1338] px-3 py-2.5" data-testid={`callin-req-${c.id}`}>
                          <span className="text-sm font-semibold truncate flex-1">{c.user_name}</span>
                          <button onClick={() => approveCall(c)} data-testid={`approve-call-${c.id}`} className="px-3 py-1.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors">Let speak</button>
                          <button onClick={() => endCall(c)} data-testid={`decline-call-${c.id}`} className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><PhoneOff className="w-4 h-4" /></button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* On air */}
                <div>
                  <p className="overline text-emerald-400 mb-3 flex items-center gap-1.5"><Radio className="w-3.5 h-3.5" /> On air ({liveCalls.length})</p>
                  <div className="space-y-2" data-testid="callin-onair">
                    {liveCalls.length === 0 ? <p className="text-xs text-zinc-600">No callers on air yet.</p> :
                      liveCalls.map((c) => {
                        const lvl = c.muted ? 0 : (callerLevels[c.user_id] ?? 1);
                        const clip = !c.muted && lvl >= 1.6;
                        return (
                        <div key={c.id} className="rounded-xl bg-[#0C1338] px-3 py-2.5 space-y-2" data-testid={`callin-live-${c.id}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${c.status === "live" ? "bg-emerald-400 live-dot" : "bg-[#E6B34A]"}`} />
                            <span className="text-sm font-semibold truncate flex-1">{c.user_name}{c.status === "granted" && <span className="text-zinc-500 font-normal"> · joining…</span>}</span>
                            <button onClick={() => toggleCallerMusic(c)} data-testid={`caller-music-${c.id}`} title={callerMusic[c.user_id] ? "Music mode ON — full stereo, clean sound, gentle duck" : "Music mode — full stereo for singing / instruments"} className={`p-1.5 rounded-lg transition-colors ${callerMusic[c.user_id] ? "bg-[#9D4EDD]/20 text-[#C08BEE]" : "hover:bg-white/10 text-zinc-500"}`}><Music className="w-4 h-4" /></button>
                            <button onClick={() => toggleCallerAutoLevel(c)} data-testid={`caller-autolevel-${c.id}`} title={(callerAutoLevel[c.user_id] ?? autoLevel) ? "Auto-level ON — tap to bypass (e.g. music guest)" : "Auto-level OFF — tap to smooth"} className={`p-1.5 rounded-lg transition-colors ${(callerAutoLevel[c.user_id] ?? autoLevel) ? "bg-[#E6B34A]/15 text-[#E6B34A]" : "hover:bg-white/10 text-zinc-500"}`}><Wand2 className="w-4 h-4" /></button>
                            <button onClick={() => toggleMuteCall(c)} data-testid={`mute-call-${c.id}`} className={`p-1.5 rounded-lg ${c.muted ? "bg-[#FF3B30]/15 text-[#FF3B30]" : "hover:bg-white/10 text-zinc-300"}`}>{c.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</button>
                            <button onClick={() => endCall(c)} data-testid={`end-call-${c.id}`} className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><PhoneOff className="w-4 h-4" /></button>
                          </div>
                          <div className="flex items-center gap-2 pl-4">
                            <Volume2 className={`w-3.5 h-3.5 shrink-0 ${clip ? "text-[#FF7A70]" : "text-zinc-500"}`} />
                            <input
                              type="range" min="0" max="2" step="0.05"
                              value={callerLevels[c.user_id] ?? 1}
                              disabled={c.muted}
                              onChange={(e) => setCallerLevel(c, parseFloat(e.target.value))}
                              data-testid={`caller-level-${c.id}`}
                              className={`flex-1 h-1.5 disabled:opacity-40 ${clip ? "accent-[#FF3B30]" : "accent-[#E6B34A]"}`}
                            />
                            <span className={`text-[10px] font-mono w-9 text-right tabular-nums ${clip ? "text-[#FF7A70] font-bold" : "text-zinc-500"}`}>{Math.round(lvl * 100)}%</span>
                          </div>
                          {clip && (
                            <div className="flex items-center gap-1.5 pl-4 text-[11px] text-[#FF7A70]" data-testid={`clip-warning-${c.id}`}>
                              <AlertTriangle className="w-3 h-3 shrink-0" /> Running hot — ease back below 150% so the mix doesn't clip.
                            </div>
                          )}
                        </div>
                        );
                      })}
                  </div>
                </div>

                {/* Invite listeners */}
                <div>
                  <p className="overline text-zinc-400 mb-3 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> Invite a listener ({inviteable.length})</p>
                  <div className="space-y-2" data-testid="callin-roster">
                    {inviteable.length === 0 ? <p className="text-xs text-zinc-600">No listeners available to invite.</p> :
                      inviteable.map((u) => (
                        <div key={u.user_id} className="flex items-center gap-2 rounded-xl bg-[#0C1338] px-3 py-2.5" data-testid={`roster-${u.user_id}`}>
                          <span className="text-sm font-semibold truncate flex-1">{u.user_name}</span>
                          <button onClick={() => inviteCall(u)} data-testid={`invite-call-${u.user_id}`} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold transition-colors">Invite</button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="episodes" className="mt-6">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 rounded-2xl border border-white/5 bg-[#141C42] p-6 h-fit">
              <h3 className="font-display font-bold text-lg mb-4">Publish an episode</h3>
              <label className="overline text-zinc-400">Title</label>
              <Input value={epTitle} onChange={(e) => setEpTitle(e.target.value)} data-testid="ep-title" className="mt-1.5 mb-3 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              <label className="overline text-zinc-400">Description</label>
              <Textarea value={epDesc} onChange={(e) => setEpDesc(e.target.value)} data-testid="ep-desc" className="mt-1.5 mb-3 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              <label className="overline text-zinc-400">Series (optional)</label>
              <Input value={epSeries} onChange={(e) => setEpSeries(e.target.value)} placeholder="e.g. Grace & Grit" data-testid="ep-series" className="mt-1.5 mb-3 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
              <label className="overline text-zinc-400">Audio (mp3 / wav)</label>
              <input ref={epFileRef} type="file" accept="audio/*" data-testid="ep-file" className="mt-1.5 mb-3 w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E6B34A] file:text-[#0A1030] file:font-semibold" />
              <label className="overline text-zinc-400">Cover art (optional)</label>
              <input ref={coverRef} type="file" accept="image/*" data-testid="ep-cover" className="mt-1.5 mb-4 w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-white/10 file:text-white file:font-semibold" />
              <button onClick={uploadEpisode} disabled={uploading} data-testid="ep-upload" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Publish
              </button>
            </div>
            <div className="lg:col-span-3 space-y-3" data-testid="host-episodes">
              {episodes.length === 0 ? <p className="text-zinc-500 py-10 text-center border border-dashed border-white/10 rounded-2xl">No episodes yet.</p> :
                episodes.map((ep) => (
                  <div key={ep.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#0C1338] border border-white/10 flex items-center justify-center shrink-0">
                        {ep.has_cover ? <img src={`${api.defaults.baseURL}${ep.cover_url}?b=${coverBust}`} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-zinc-600" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-bold truncate">{ep.title}</h3>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => regenCover(ep.id)} data-testid={`regen-cover-${ep.id}`} title="Re-roll auto cover" className="p-2 rounded-lg hover:bg-white/10 text-zinc-300"><Wand2 className="w-4 h-4" /></button>
                            <button onClick={() => setEditEp({ id: ep.id, title: ep.title, description: ep.description || "", series: ep.series || "", coverFile: null })} data-testid={`edit-ep-${ep.id}`} className="p-2 rounded-lg hover:bg-white/10 text-zinc-300"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => delEpisode(ep.id)} data-testid={`del-ep-${ep.id}`} className="p-2 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        <p className="text-xs text-zinc-500">by {ep.host_name}</p>
                        {ep.description && <p className="text-sm text-zinc-400 mt-1">{ep.description}</p>}
                      </div>
                    </div>
                    <audio controls className="w-full mt-3 h-10"><source src={`${api.defaults.baseURL}/episodes/${ep.id}/audio`} /></audio>
                  </div>
                ))}
            </div>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="hosts" className="mt-6">
            <div className="space-y-3" data-testid="hosts-list">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-4 rounded-2xl border border-white/5 bg-[#141C42] p-4">
                  <ShieldCheck className={`w-5 h-5 ${u.role === "user" ? "text-zinc-600" : "text-[#E6B34A]"}`} />
                  <div className="min-w-0 flex-1"><p className="font-semibold truncate">{u.name}</p><p className="text-xs text-zinc-500 truncate">{u.email}</p></div>
                  <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} data-testid={`role-${u.id}`} className="rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                    <option value="user">Listener</option>
                    <option value="host">Host</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="schedule-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-[#E6B34A]" /> Schedule a live show</DialogTitle></DialogHeader>
          <div className="mt-2">
            <label className="overline text-zinc-400">Title</label>
            <Input value={sched.title} onChange={(e) => setSched({ ...sched, title: e.target.value })} data-testid="sched-title" className="mt-1.5 mb-3 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            <label className="overline text-zinc-400">Date & time</label>
            <Input type="datetime-local" value={sched.scheduled_at} onChange={(e) => setSched({ ...sched, scheduled_at: e.target.value })} data-testid="sched-time" className="mt-1.5 mb-3 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            <label className="overline text-zinc-400">Description</label>
            <Textarea value={sched.description} onChange={(e) => setSched({ ...sched, description: e.target.value })} data-testid="sched-desc" className="mt-1.5 mb-3 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            <label className="overline text-zinc-400">Remind followers before show</label>
            <select value={sched.reminder_minutes} onChange={(e) => setSched({ ...sched, reminder_minutes: e.target.value })} data-testid="sched-reminder" className="mt-1.5 mb-4 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
              {[5, 10, 15, 30, 60].map((m) => <option key={m} value={m}>{m} minutes before</option>)}
            </select>
            {soundProfiles.length > 0 && (
              <>
                <label className="overline text-zinc-400">Sound profile for this show (optional)</label>
                <select value={sched.sound_profile_id || ""} onChange={(e) => setSched({ ...sched, sound_profile_id: e.target.value })} data-testid="sched-profile" className="mt-1.5 mb-4 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  <option value="">— None (use current setup) —</option>
                  {soundProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </>
            )}
            {holdLibrary.length > 0 && (
              <>
                <label className="overline text-zinc-400">Hold music for this show (optional)</label>
                <select value={sched.hold_track_id || ""} onChange={(e) => setSched({ ...sched, hold_track_id: e.target.value })} data-testid="sched-hold-track" className="mt-1.5 mb-4 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
                  <option value="">— None (keep current hold music) —</option>
                  {holdLibrary.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            )}
            <label className="overline text-zinc-400">Cover art (optional)</label>
            <input ref={schedCoverRef} type="file" accept="image/*" className="hidden" data-testid="sched-cover-file" onChange={(e) => setSchedCoverName(e.target.files[0]?.name || "")} />
            <button type="button" onClick={() => schedCoverRef.current?.click()} data-testid="sched-cover-btn" className="mt-1.5 mb-4 w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E2A5A] border border-white/10 text-sm text-zinc-300 hover:border-[#E6B34A] transition-colors">
              <ImageIcon className="w-4 h-4 text-[#E6B34A]" /> {schedCoverName || "Add a cover image"}
            </button>
            <button onClick={createSchedule} data-testid="sched-save" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">Add to Guide</button>
            {schedules.length > 0 && (
              <div className="mt-5 space-y-2" data-testid="sched-list">
                {schedules.map((s) => {
                  const prof = s.sound_profile_id && soundProfiles.find((p) => p.id === s.sound_profile_id);
                  return (
                  <div key={s.id} className="flex items-center gap-3 rounded-xl bg-[#0C1338] px-4 py-3">
                    <Clock className="w-4 h-4 text-[#E6B34A] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{s.title}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs text-zinc-500">{fmt(s.scheduled_at)}</p>
                        {prof && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E6B34A]/15 text-[#E6B34A] text-[10px] font-bold" data-testid={`sched-profile-badge-${s.id}`}>
                            <Gauge className="w-2.5 h-2.5" /> {prof.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => delSchedule(s.id)} data-testid={`del-sched-${s.id}`} className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={!!editEp} onOpenChange={(o) => !o && setEditEp(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="episode-edit-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">Edit Episode</DialogTitle></DialogHeader>
          {editEp && (
            <div className="space-y-3 mt-2">
              <div><label className="overline text-zinc-400">Title</label><Input value={editEp.title} onChange={(e) => setEditEp({ ...editEp, title: e.target.value })} data-testid="edit-ep-title" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div><label className="overline text-zinc-400">Series</label><Input value={editEp.series} onChange={(e) => setEditEp({ ...editEp, series: e.target.value })} data-testid="edit-ep-series" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div><label className="overline text-zinc-400">Description</label><Textarea value={editEp.description} onChange={(e) => setEditEp({ ...editEp, description: e.target.value })} data-testid="edit-ep-desc" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" /></div>
              <div>
                <label className="overline text-zinc-400">Replace cover (optional)</label>
                <input ref={editCoverRef} type="file" accept="image/*" className="hidden" data-testid="edit-ep-cover-file" onChange={(e) => setEditEp({ ...editEp, coverFile: e.target.files[0] })} />
                <button type="button" onClick={() => editCoverRef.current?.click()} data-testid="edit-ep-cover-btn" className="mt-1.5 w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0C1338] border border-white/10 text-sm text-zinc-300 hover:border-[#E6B34A] transition-colors">
                  <ImageIcon className="w-4 h-4 text-[#E6B34A]" /> {editEp.coverFile ? editEp.coverFile.name : "Change cover image"}
                </button>
              </div>
              <button onClick={saveEp} disabled={savingEp} data-testid="save-ep-edit" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {savingEp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewProfile} onOpenChange={(o) => !o && setPreviewProfile(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="profile-preview-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">{previewProfile?.name}</DialogTitle></DialogHeader>
          {previewProfile?.shared_by && <p className="text-xs text-zinc-400 -mt-2">Shared by {previewProfile.shared_by}</p>}
          <div className="mt-2 divide-y divide-white/5 rounded-xl border border-white/5 overflow-hidden">
            {describeSettings(previewProfile?.settings).map(([label, value]) => (
              <div key={label} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`preview-row-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="text-zinc-400">{label}</span>
                <span className="font-semibold">{value}</span>
              </div>
            ))}
            {describeSettings(previewProfile?.settings).length === 0 && <p className="px-4 py-3 text-sm text-zinc-500">No settings stored.</p>}
          </div>
          <button onClick={() => { const p = previewProfile; setPreviewProfile(null); if (p) { if (inbox.some((x) => x.id === p.id)) acceptInbox(p); else applySoundProfile(p); } }} data-testid="preview-apply" className="mt-3 w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
            {previewProfile && inbox.some((x) => x.id === previewProfile.id) ? "Add to my profiles" : "Apply this profile"}
          </button>
        </DialogContent>
      </Dialog>

      {pendingRec && (
        <RecordingReview
          blob={pendingRec.blob}
          defaultTitle={pendingRec.defaultTitle}
          tracks={pendingRec.tracks}
          onClose={() => setPendingRec(null)}
          onPublished={() => { setPendingRec(null); loadEpisodes(); }}
        />
      )}
    </div>
  );
}
